// =====================================================================
// EGL v12 Frontend - CERTIFICATE CREATION FORM
// Purpose: Complete certificate creation with unified S3 integration
// =====================================================================

import React, { useState, useEffect, useCallback } from 'react'
import { useRouter } from '../lib/SmartRouter'
import { certificateService } from '../services/certificateService'
import { TokenManager } from '../lib/tokenManager'
import { SessionManager } from '../lib/sessionManager'
import { ReAuthModal } from './ReAuthModal'
import { SessionExtensionModal } from './SessionExtensionModal'
import { CertificateLivePreview } from './CertificateLivePreview'
import { ImageCropModal } from './ImageCropModal'
import { StagingImageUpload } from './StagingImageUpload'
import { CommentLineGenerator } from '../utils/commentLineGenerator'
import { AbbreviationLineGenerator } from '../utils/abbreviationLineGenerator'
import type { 
  CertificateFormData, 
  GemstoneSelection,
  GemstoneConfig,
  GemstoneType, 
  MetalComposition, 
  GemstoneGrowthOrigin 
} from '../types/certificate'
import './CertificateCreationForm.css'

// Enhanced validation schema with real-time validation
const validationSchema = {
  cert_id: (value: string) => {
    if (!value) return { valid: false, message: 'Certificate ID is required', code: 'CERT_001' }
    if (!/^[A-Z0-9]+$/.test(value)) return { valid: false, message: 'Only uppercase letters and numbers allowed', code: 'CERT_002' }
    if (value.length < 3 || value.length > 15) return { valid: false, message: 'Certificate ID must be 3-15 characters', code: 'CERT_003' }
    return { valid: true, message: 'Certificate ID format is valid', code: 'CERT_SUCCESS' }
  },
  
  gemstone_types: (values: string[]) => {
    if (values.length === 0) return { valid: false, message: 'At least one gemstone type is required', code: 'GEM_001' }
    if (values.length > 3) return { valid: false, message: 'Maximum 3 gemstone types allowed', code: 'GEM_002' }
    return { valid: true, message: `${values.length} gemstone type(s) selected`, code: 'GEM_SUCCESS' }
  },
  
  gemstone_gross_weight: (value: string) => {
    if (!value) return { valid: false, message: 'Gemstone gross weight is required', code: 'GEM_003' }
    const num = parseFloat(value)
    if (isNaN(num) || num <= 0 || num > 9999.99) return { valid: false, message: 'Weight must be between 0.01 and 9999.99 carats', code: 'GEM_004' }
    // Allow properly formatted decimal values (including .00 format)
    if (!/^\d{1,4}(\.\d{2})?$/.test(value) && !/^\d{1,4}\.\d{1}$/.test(value) && !/^\d{1,4}$/.test(value)) {
      return { valid: false, message: 'Weight must be a valid number up to 9999.99', code: 'GEM_005' }
    }
    return { valid: true, message: 'Weight is valid', code: 'GEM_SUCCESS' }
  },
  
  item_gross_weight: (value: string) => {
    // Handle empty/cleared values - Show simple required message
    if (!value || value.trim() === '') return {
      valid: false,
      message: 'Weight is required.',
      code: 'MET_002'
    }

    const num = parseFloat(value)
    
    // Check for invalid number format
    if (isNaN(num)) return {
      valid: false,
      message: 'Please enter a valid weight (e.g., 1.50, 25.75)',
      code: 'MET_003'
    }

    // Check range constraints
    if (num < 0.01 || num > 9999.99) return {
      valid: false,
      message: 'Weight must be 0.01-9999.99 GRAMS',
      code: 'MET_004'
    }

    // Allow properly formatted decimal values (including partial typing like "1." or "1.5")
    if (!/^\d{1,4}(\.\d{0,2})?$/.test(value)) {
      return {
        valid: false,
        message: 'Weight format should be up to 9999.99 GRAMS',
        code: 'MET_005'
      }
    }

    return { valid: true, message: 'Weight is valid', code: 'MET_SUCCESS' }
  }
}

interface ValidationState {
  [key: string]: {
    isValid: boolean
    message: string
    code: string
    isChecking?: boolean
  }
}

interface StatusMessage {
  type: 'error' | 'success' | 'info' | 'warning'
  message: string
  code?: string
  timestamp: number
}

interface ImageUploadState {
  file: File | null
  selected: boolean      // File is selected and preview is shown
  uploaded: boolean      // File is uploaded to S3
  uploading: boolean     // Currently uploading to S3
  presignedUrl: string | null
  originalFilename: string | null
  systemFilename: string | null
  s3Key: string | null
  uploadId: string | null
}

export const CertificateCreationForm: React.FC = () => {
  const { user, logout, navigate } = useRouter()
  
  // State for reference data from database
  const [referenceData, setReferenceData] = useState<{
    gemstone_types: Array<{id: number, gemstone_type: string, abbreviation: string, description: string}>,
    color_ranges: Array<{id: number, color_range: string, abbreviation: string, description: string}>,
    clarity_ranges: Array<{id: number, clarity_range: string, abbreviation: string, description: string}>,
    growth_origins: Array<{id: number, growth_origin: string, abbreviation: string, description: string}>,
    metal_compositions: Array<{id: number, metal_composition: string, abbreviation: string, description: string}>
  }>({
    gemstone_types: [],
    color_ranges: [],
    clarity_ranges: [],
    growth_origins: [],
    metal_compositions: []
  })

  // State for gemstone configurations (measurement types)
  const [gemstoneConfigs, setGemstoneConfigs] = useState<GemstoneConfig[]>([])
  
  // State for diamond reference data
  const [diamondReferenceData, setDiamondReferenceData] = useState<{
    diamond_colors: Array<{color_code: string, description: string}>,
    diamond_clarity: Array<{clarity_code: string, description: string}>,
    diamond_color_ranges: Array<{range_code: string, description: string}>,
    diamond_clarity_ranges: Array<{range_code: string, description: string}>,
    defaults: {color_range: string, clarity_range: string}
  }>({
    diamond_colors: [],
    diamond_clarity: [],
    diamond_color_ranges: [],
    diamond_clarity_ranges: [],
    defaults: {color_range: 'G-H', clarity_range: 'VS-SI'}
  })
  
  const [formData, setFormData] = useState<CertificateFormData>({
    cert_id: '',
    cert_id_confirm: '',
    gemstone_selections: [], // Array of {gemstone_type, carat_weight}
    metal_composition: '18K WHITE GOLD',
    item_gross_weight: '',
    gemstone_growth_origin: 'NATURAL',
    card_design_code: 'EUROPEANGEMOLOGICALLAB',

    comment_line_1: '',
    comment_line_2: '',
    comment_line_3: '',
    abbreviation_line_1: '',
    abbreviation_line_2: '',
    abbreviation_line_3: '',
    abbreviation_line_4: '',
    abbreviation_line_5: '',
    abbreviation_line_6: '',
    abbreviation_line_7: '',
    abbreviation_line_8: '',
    abbreviation_line_9: '',
    abbreviation_line_10: '',
    abbreviation_line_11: '',
    abbreviation_line_12: '',
    abbreviation_line_13: ''
  });

  // Field states for auto-populated vs user-editable fields
  const [fieldStates, setFieldStates] = useState({
    comment_line_1: { isAutoPopulated: false, isEditable: true },
    comment_line_2: { isAutoPopulated: false, isEditable: true },
    comment_line_3: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_1: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_2: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_3: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_4: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_5: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_6: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_7: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_8: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_9: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_10: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_11: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_12: { isAutoPopulated: false, isEditable: true },
    abbreviation_line_13: { isAutoPopulated: false, isEditable: true }
  });

  // Calculate running total of gemstone weights (only for weight-based gemstones)
  const calculateRunningTotal = (): string => {
    const total = formData.gemstone_selections.reduce((sum, selection) => {
      const config = getGemstoneConfig(selection.gemstone_type);
      
      // Only include weight measurements, not pearl diameters
      if (config?.default_measurement_type === 'WEIGHT') {
        const weight = parseFloat(selection.carat_weight || selection.measurement_value || '0');
        return sum + (isNaN(weight) ? 0 : weight);
      }
      
      return sum; // Pearls don't contribute to weight total
    }, 0);
    return total.toFixed(2);
  };

  const [validationState, setValidationState] = useState<ValidationState>({})
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([])
  const [fieldsLocked, setFieldsLocked] = useState(false)
  const [imageUpload, setImageUpload] = useState<ImageUploadState>({
    file: null,
    selected: false,
    uploaded: false,
    uploading: false,
    presignedUrl: null,
    originalFilename: null,
    systemFilename: null,
    s3Key: null,
    uploadId: null
  })
  
  // Crop modal state
  const [showCropModal, setShowCropModal] = useState<boolean>(false)
  const [selectedFileForCrop, setSelectedFileForCrop] = useState<File | null>(null)
  
  const [certificateCreated, setCertificateCreated] = useState(false)
  const [certificateUuid, setCertificateUuid] = useState<string | null>(null)
  const [showImageUpload, setShowImageUpload] = useState(false)
  const [uploadMethod, setUploadMethod] = useState<'computer' | 'staging'>('computer')
  const [showVerificationCheckpoint, setShowVerificationCheckpoint] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sessionWarning, setSessionWarning] = useState<string | null>(null)
  const [showReAuthModal, setShowReAuthModal] = useState(false)
  const [showSessionExtension, setShowSessionExtension] = useState(false)
  const [sessionTimeLeft, setSessionTimeLeft] = useState<string>('')

  // Fetch reference data from database
  const fetchReferenceData = async () => {
    try {
      console.log('🔍 Fetching reference data from database...')
      const response = await fetch('/api/public/reference-data')
      const data = await response.json()
      
      if (data.success) {
        console.log('✅ Reference data loaded:', data.data)
        setReferenceData(data.data)
        
        // Update form defaults with first available options

        if (data.data.growth_origins.length > 0) {
          setFormData(prev => ({ ...prev, gemstone_growth_origin: data.data.growth_origins[0].growth_origin }))
        }
        if (data.data.metal_compositions.length > 0) {
          setFormData(prev => ({ ...prev, metal_composition: data.data.metal_compositions[0].metal_composition }))
        }
      } else {
        console.error('❌ Failed to fetch reference data:', data.message)
      }
    } catch (error) {
      console.error('❌ Error fetching reference data:', error)
    }
  }

  // Fetch gemstone configurations from database
  const fetchGemstoneConfigs = async () => {
    try {
      console.log('🔍 Fetching gemstone configurations...')
      const result = await certificateService.getGemstoneConfigurations()
      if (result.success && result.data) {
        console.log('✅ Gemstone configurations loaded:', result.data)
        setGemstoneConfigs(result.data)
      } else {
        console.error('❌ Failed to fetch gemstone configurations:', result.message)
      }
    } catch (error) {
      console.error('❌ Error fetching gemstone configurations:', error)
    }
  }

  // Debug: Log component mount and state
  React.useEffect(() => {
    console.log('🚀 CertificateCreationForm mounted!')
    console.log('🔍 Initial fieldsLocked:', fieldsLocked)
    console.log('🔍 Initial formData:', formData)
    console.log('🔍 User data from router:', user)
    
    // Fetch reference data and configurations on mount
    fetchReferenceData()
    fetchGemstoneConfigs()
    fetchDiamondReferenceData()
    
    // Trigger initial validation for weight fields
    setTimeout(() => {
      validateWeightField('item_gross_weight', formData.item_gross_weight)
    }, 100)
  }, [])

  React.useEffect(() => {
    console.log('🔍 fieldsLocked changed:', fieldsLocked)
  }, [fieldsLocked])

  // Enhanced session management with extension capabilities
  React.useEffect(() => {
    const cleanup = SessionManager.startSessionMonitoring({
      onShowExtensionButton: (timeLeft: string) => {
        setSessionTimeLeft(timeLeft)
        setShowSessionExtension(true)
        setSessionWarning(`Session expires in ${timeLeft}`)
      },
      onHideExtensionButton: () => {
        setShowSessionExtension(false)
        setSessionWarning(null)
      },
      onSessionExpired: () => {
        console.log('🔍 onSessionExpired callback triggered!')
        console.log('🔍 DEBUGGING: Session expiry callback - TEMPORARILY DISABLED')
        setShowSessionExtension(false)
        setSessionWarning(null)
        addStatusMessage('Your session has expired. Please log in again.', 'error')
        // TEMPORARILY DISABLED FOR DEBUGGING
        console.log('🔍 Login redirect TEMPORARILY DISABLED for debugging')
        // setTimeout(() => {
        //   window.location.href = '/login'
        // }, 3000)
      },
      onExtensionSuccess: (newToken: string) => {
        console.log('✅ Session extension successful!')
        setShowSessionExtension(false)
        setSessionWarning(null)
        addStatusMessage('✅ Session extended for another 15 minutes!', 'success')
      },
      onExtensionFailed: (error: string) => {
        console.error('❌ Session extension failed:', error)
        addStatusMessage(`❌ Session extension failed: ${error}`, 'error')
        // Keep modal open so user can try again
      }
    })

    return cleanup
  }, [])

  React.useEffect(() => {
    console.log('🔍 User data updated:', user)
  }, [user])

  // Debounced validation function
  const debounce = useCallback((func: Function, wait: number) => {
    let timeout: NodeJS.Timeout
    return (...args: any[]) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }, [])

  // Immediate validation for weight fields (no debounce)
  const validateWeightField = useCallback((field: string, value: string) => {
    if (field !== 'item_gross_weight' && field !== 'gemstone_gross_weight') {
      return // Only handle weight fields
    }
    
    const validation = validationSchema.item_gross_weight(value)
    
    setValidationState(prev => ({
      ...prev,
      [field]: { ...validation, isValid: validation.valid, isChecking: false }
    }))
  }, [])

  // Immediate validation for Certificate ID (no debounce for format validation)
  const validateCertIdField = useCallback(async (field: string, value: string) => {
    if (field !== 'cert_id') {
      return // Only handle cert_id field
    }
    
    // First, validate format immediately
    const formatValidation = validationSchema.cert_id(value)
    
    if (!formatValidation.valid || value.length < 3) {
      // Show format validation immediately
      setValidationState(prev => ({
        ...prev,
        [field]: { ...formatValidation, isValid: formatValidation.valid, isChecking: false }
      }))
      return
    }
    
    // Show checking state for server validation
    setValidationState(prev => ({
      ...prev,
      [field]: { ...formatValidation, isValid: formatValidation.valid, isChecking: true }
    }))
    
    // Check uniqueness with server
    try {
      const uniqueCheck = await certificateService.validateCertificateId(value)
      const serverValidation = {
        valid: uniqueCheck.available,
        message: uniqueCheck.available ? 'Certificate ID is available' : 'Certificate ID already exists',
        code: uniqueCheck.available ? 'CRT_SUCCESS' : 'CRT_003'
      }
      
      setValidationState(prev => ({
        ...prev,
        [field]: { ...serverValidation, isValid: serverValidation.valid, isChecking: false }
      }))
    } catch (error) {
      const errorValidation = { valid: false, message: 'Unable to verify certificate ID', code: 'CRT_ERROR' }
      setValidationState(prev => ({
        ...prev,
        [field]: { ...errorValidation, isValid: errorValidation.valid, isChecking: false }
      }))
    }
  }, [])

  // Real-time validation (debounced for non-weight fields)
  const validateField = useCallback(
    debounce(async (field: string, value: any) => {
      let validation: any

      switch (field) {
        case 'cert_id':
          // Certificate ID validation is now handled by validateCertIdField for immediate feedback
          validation = { valid: true, message: 'Handled by immediate validation', code: 'SUCCESS' }
          break
        case 'gemstone_types':
          validation = validationSchema.gemstone_types(value)
          break
        case 'gemstone_gross_weight':
          validation = validationSchema.gemstone_gross_weight(value)
          break
        case 'item_gross_weight':
          validation = validationSchema.item_gross_weight(value)
          break
        default:
          validation = { valid: true, message: '', code: 'SUCCESS' }
      }

      setValidationState(prev => ({
        ...prev,
        [field]: { ...validation, isValid: validation.valid, isChecking: false }
      }))
    }, 100),
    []
  )

  // Trigger validation when form data changes
  React.useEffect(() => {
    // Validate all fields that have values
    Object.entries(formData).forEach(([field, value]) => {
      if (value && typeof value === 'string' && value.trim() !== '') {
        validateField(field, value)
      } else if (field === 'item_gross_weight' || field === 'gemstone_gross_weight') {
        // Always validate weight fields, even if empty, to show helpful messages
        validateField(field, value as string)
      }
    })
  }, [formData])

  // Format gemstone types alphabetically (DIAMOND generic type removed)
  const formatGemstoneTypes = (types: string[]): string[] => {
    return types.sort()
  }

  // Handle gemstone type selection (checkbox)
  const handleGemstoneTypeChange = (type: string, checked: boolean) => {
    console.log('🔍 Gemstone type change:', type, checked)
    
    if (fieldsLocked) {
      console.log('🔒 Fields are locked, ignoring change')
      return
    }

    const config = getGemstoneConfig(type);
    
    setFormData(prev => {
      let newSelections = [...prev.gemstone_selections];
      
      if (checked) {
        // Add new gemstone with appropriate measurement type
        const existingSelection = newSelections.find(s => s.gemstone_type === type);
        if (!existingSelection && newSelections.length < 3) {
          if (config?.default_measurement_type === 'DIAMETER') {
            // Pearl - start with diameter range as default
            newSelections.push({
              gemstone_type: type,
              measurement_type: 'DIAMETER_RANGE',
              measurement_range: '-',
              measurement_unit: 'mm'
            });
          } else {
            // Traditional gemstone - start with empty values and default growth origin
            const newSelection: any = {
              gemstone_type: type,
              growth_origin: 'NATURAL', // Default to NATURAL
              measurement_type: 'WEIGHT',
              measurement_value: '',
              measurement_unit: 'carats',
              carat_weight: '' // Backward compatibility
            };
            
            // Add diamond-specific defaults if it's a diamond
            if (type.startsWith('DIAMOND_')) {
              newSelection.diamond_color_type = 'RANGE';
              newSelection.diamond_color_range = 'G-H';
              newSelection.diamond_clarity_type = 'RANGE';
              newSelection.diamond_clarity_range = 'VS-SI';
            }
            
            newSelections.push(newSelection);
          }
        }
      } else {
        // Remove gemstone
        newSelections = newSelections.filter(s => s.gemstone_type !== type);
        
        // Handle cascading deselection for diamonds
        if (type === 'DIAMOND_1') {
          // If Diamond 1 is deselected, also remove Diamond 2 and Diamond 3
          newSelections = newSelections.filter(s => s.gemstone_type !== 'DIAMOND_2' && s.gemstone_type !== 'DIAMOND_3');
          console.log('🔍 Cascading deselection: Diamond 1 removed, also removing Diamond 2 and 3');
        } else if (type === 'DIAMOND_2') {
          // If Diamond 2 is deselected, also remove Diamond 3
          newSelections = newSelections.filter(s => s.gemstone_type !== 'DIAMOND_3');
          console.log('🔍 Cascading deselection: Diamond 2 removed, also removing Diamond 3');
        }
      }
      
      // Auto-calculate certificate-level growth origin
      const certificateGrowthOrigin = autoSelectCertificateGrowthOrigin(newSelections);
      
      console.log('🔍 New gemstone selections:', newSelections);
      console.log('🔍 Auto-calculated growth origin:', certificateGrowthOrigin);
      
      return { 
        ...prev, 
        gemstone_selections: newSelections,
        gemstone_growth_origin: certificateGrowthOrigin
      };
    });
  };

  // Enhanced contextual validation messages with measurement type awareness
  const getValidationMessage = (value: string, gemstoneType: string) => {
    const config = getGemstoneConfig(gemstoneType);
    const isPearl = config?.default_measurement_type === 'DIAMETER';
    const unit = isPearl ? 'mm' : 'carats';
    const measurementName = isPearl ? 'diameter' : 'weight';
    
    // Handle empty/cleared values - Show helpful message with uncheck option
    if (!value || value.trim() === '') return {
      type: 'error' as const,
      message: isPearl 
        ? `Enter Pearl Diameter Range. If you don't need this field, uncheck it from above.`
        : `Weight is required. If you don't need this field, uncheck it from above.`,
      allowSubmit: false,
      requiresVerification: false
    };
    
    // Handle range format for pearls
    if (isPearl && value.includes('-')) {
      const rangeMatch = value.match(/^(\d+\.?\d*)-(\d+\.?\d*)$/);
      if (!rangeMatch) {
        return {
          type: 'error' as const,
          message: `Invalid range format. Use: min-max (e.g., "2.50-5.75")`,
          allowSubmit: false,
          requiresVerification: false
        };
      }
      
      const minVal = parseFloat(rangeMatch[1]);
      const maxVal = parseFloat(rangeMatch[2]);
      
      if (minVal >= maxVal) {
        return {
          type: 'error' as const,
          message: `Minimum must be less than maximum. Try: ${config?.min_value || 0.10}-${config?.max_value || 30.00}`,
          allowSubmit: false,
          requiresVerification: false
        };
      }
      
      if (minVal < (config?.min_value || 0.10) || maxVal > (config?.max_value || 30.00)) {
        return {
          type: 'error' as const,
          message: `Range must be ${config?.min_value || 0.10}-${config?.max_value || 30.00} ${unit}`,
          allowSubmit: false,
          requiresVerification: false
        };
      }
      
      return {
        type: 'success' as const,
        message: `Diameter range looks good`,
        allowSubmit: true,
        requiresVerification: false
      };
    }
    
    const num = parseFloat(value);
    
    // Handle invalid numbers
    if (isNaN(num)) return {
      type: 'error' as const,
      message: `Invalid ${measurementName} format. Enter a number between ${config?.min_value || 0.01}-${config?.max_value || 9999.99} ${unit}`,
      allowSubmit: false,
      requiresVerification: false
    };
    
    // Handle out of range values
    const minVal = config?.min_value || (isPearl ? 0.10 : 0.01);
    const maxVal = config?.max_value || (isPearl ? 30.00 : 9999.99);
    
    if (num < minVal || num > maxVal) return {
      type: 'error' as const,
      message: `${measurementName.charAt(0).toUpperCase() + measurementName.slice(1)} must be ${minVal}-${maxVal} ${unit}`,
      allowSubmit: false,
      requiresVerification: false
    };
    
    // Contextual warnings for pearls
    if (isPearl) {
      if (num <= 1.00) return {
        type: 'warning' as const,
        message: `Very small diameter - please verify this is correct`,
        allowSubmit: true,
        requiresVerification: true
      };
      
      if (num >= 20.00) return {
        type: 'warning' as const,
        message: `Very large diameter - please verify this is correct`,
        allowSubmit: true,
        requiresVerification: true
      };
    } else {
      // Contextual warnings for traditional gemstones
      if (num <= 0.05) return {
        type: 'warning' as const,
        message: `Very small weight - please verify this is correct`,
        allowSubmit: true,
        requiresVerification: true
      };
      
      if (num >= 100.00) return {
        type: 'warning' as const,
        message: `Very large weight - please verify this is correct`,
        allowSubmit: true,
        requiresVerification: true
      };
    }
    
    // All good
    return {
      type: 'success' as const,
      message: `${measurementName.charAt(0).toUpperCase() + measurementName.slice(1)} looks good`,
      allowSubmit: true,
      requiresVerification: false
    };
  };

  // Get gemstone configuration by type
  const getGemstoneConfig = (gemstoneType: string): GemstoneConfig | null => {
    return gemstoneConfigs.find(config => config.gemstone_type === gemstoneType) || null;
  };

  // Get validation state for a gemstone measurement
  const getWeightValidation = (gemstoneType: string) => {
    const selection = formData.gemstone_selections.find(s => s.gemstone_type === gemstoneType);
    const config = getGemstoneConfig(gemstoneType);
    
    if (!selection || !config) return null;
    
    // For pearls, validate diameter measurements
    if (config.default_measurement_type === 'DIAMETER') {
      if (config.supports_ranges) {
        // Range validation - always validate, even if empty
        return getValidationMessage(selection.measurement_range || '', gemstoneType);
      } else {
        // Single diameter validation - always validate, even if empty
        return getValidationMessage(selection.measurement_value || '', gemstoneType);
      }
    }
    
    // For traditional gemstones, validate weight - always validate, even if empty
    const weightValue = selection.carat_weight || selection.measurement_value || '';
    return getValidationMessage(weightValue, gemstoneType);
  };

  // Handle single selection fields
  const handleSingleSelection = (field: string, value: string) => {
    console.log('🔍 Radio button clicked:', field, value)
    if (fieldsLocked) {
      console.log('🔒 Fields are locked!')
      return
    }
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Handle weight input with decimal formatting
  const handleWeightInput = (field: string, value: string) => {
    if (fieldsLocked) return
    
    // Allow complete clearing of the field
    if (value === '') {
      setFormData(prev => ({ ...prev, [field]: value }))
      // Trigger immediate validation for empty field
      validateWeightField(field, value)
      return
    }
    
    // Remove any spaces
    value = value.replace(/\s/g, '')
    
    // Only allow numbers and one decimal point with max 2 decimal places, up to 9999.99
    const regex = /^\d{0,4}\.?\d{0,2}$/
    if (regex.test(value)) {
      // Additional check to ensure value doesn't exceed 9999.99 when it's a complete number
      const numValue = parseFloat(value)
      if (isNaN(numValue) || numValue <= 9999.99) {
        setFormData(prev => ({ ...prev, [field]: value }))
        // Trigger immediate validation for the new value
        validateWeightField(field, value)
      }
    }
  }

  // Format weight value to 2 decimal places for display and database
  const formatWeightValue = (value: string): string => {
    if (!value || value === '') return ''
    const numValue = parseFloat(value)
    if (isNaN(numValue)) return value
    const formatted = numValue.toFixed(2)
    console.log(`🔍 Weight formatting: "${value}" → "${formatted}"`)
    return formatted
  }

  // Handle weight input blur to format to 2 decimal places
  const handleWeightBlur = (field: string, value: string) => {
    // Always trigger immediate validation on blur
    validateWeightField(field, value)
    
    // Allow fields to remain empty - don't auto-restore defaults
    if (!value || value.trim() === '') {
      return // Let validation messages handle empty fields
    }
    
    // Only format valid numbers that meet minimum requirements (0.01 or higher)
    const numValue = parseFloat(value)
    if (!isNaN(numValue) && numValue >= 0.01) {
      const formattedValue = formatWeightValue(value)
      console.log(`🔍 Weight blur ${field}: "${value}" → "${formattedValue}"`)
      setFormData(prev => ({ ...prev, [field]: formattedValue }))
      // Validate the formatted value immediately
      validateWeightField(field, formattedValue)
    }
    // For invalid values, leave as-is and let validation messages guide the user
  }

  // Handle text input with character limits
  const handleTextInput = (field: string, value: string, maxLength: number = 20) => {
    if (fieldsLocked) return
    
    // ASCII only validation
    if (/^[\x20-\x7E]*$/.test(value) && value.length <= maxLength) {
      // Convert comment lines and abbreviation lines to uppercase
      const finalValue = (field.startsWith('comment_line_') || field.startsWith('abbreviation_line_')) 
        ? value.toUpperCase() 
        : value
      setFormData(prev => ({ ...prev, [field]: finalValue }))
    }
  }

  // Check if all mandatory fields are valid
  const checkMandatoryFieldsValid = useCallback(() => {
    const mandatoryFields = ['cert_id', 'gemstone_types', 'gemstone_gross_weight', 'item_gross_weight']
    const fieldsValid = mandatoryFields.every(field => {
      const validation = validationState[field]
      return validation && validation.isValid && !validation.isChecking
    })
    
    // Also check that cert_id_confirm matches cert_id
    const certIdConfirmValid = !!(formData.cert_id && formData.cert_id_confirm && 
                                  formData.cert_id === formData.cert_id_confirm)
    
    return fieldsValid && certIdConfirmValid
  }, [validationState, formData.cert_id, formData.cert_id_confirm])

  // Comprehensive validation including gemstone-specific requirements
  const checkAllValidationsPass = useCallback(() => {
    // First check mandatory fields
    if (!checkMandatoryFieldsValid()) {
      return false
    }

    // Check that all selected gemstones have complete data
    const allGemstonesValid = formData.gemstone_selections.every(selection => {
      if (!selection.gemstone_type) return true // Skip empty selections
      
      // For pearls, we need measurement data (range or value)
      if (selection.gemstone_type.includes('PEARL')) {
        return !!(selection.measurement_range || selection.measurement_value)
      }
      
      // For diamonds, we need weight and growth_origin and color/clarity
      if (selection.gemstone_type.startsWith('DIAMOND_')) {
        return !!(selection.carat_weight || selection.measurement_value) &&
               selection.growth_origin !== undefined && 
               (selection.diamond_color_range || selection.diamond_color_fixed || 
                selection.diamond_clarity_range || selection.diamond_clarity_fixed)
      }
      
      // For other gemstones, we need weight and growth_origin
      return !!(selection.carat_weight || selection.measurement_value) &&
             selection.growth_origin !== undefined
    })

    return allGemstonesValid
  }, [checkMandatoryFieldsValid, formData.gemstone_selections])

  // Show image upload section when validation passes or image is selected/uploaded
  useEffect(() => {
    const isValid = checkMandatoryFieldsValid()
    const shouldShow = isValid || imageUpload.selected || imageUpload.uploaded
    console.log('🔍 Image upload visibility check:', {
      isValid,
      imageSelected: imageUpload.selected,
      imageUploaded: imageUpload.uploaded,
      fieldsLocked,
      shouldShow,
      certId: formData.cert_id
    })
    // Show upload section if form is valid OR if an image is selected/uploaded
    setShowImageUpload(shouldShow)
  }, [checkMandatoryFieldsValid, fieldsLocked, imageUpload.selected, imageUpload.uploaded])

  // Auto-populate comment and abbreviation lines based on gemstone selections
  useEffect(() => {
    if (formData.gemstone_selections && formData.gemstone_selections.length > 0) {
      // Filter out incomplete selections (missing required data)
      const completeSelections = formData.gemstone_selections.filter(selection => {
        if (!selection.gemstone_type) return false;
        
        // For pearls, we need measurement data (range or value) but no growth_origin
        if (selection.gemstone_type.includes('PEARL')) {
          return !!(selection.measurement_range || selection.measurement_value || selection.carat_weight);
        }
        
        // For diamonds, we need weight and growth_origin and at least color or clarity
        if (selection.gemstone_type.startsWith('DIAMOND_')) {
          return !!(selection.carat_weight || selection.measurement_value) &&
                 selection.growth_origin !== undefined && 
                 (selection.diamond_color_range || selection.diamond_color_fixed || 
                  selection.diamond_clarity_range || selection.diamond_clarity_fixed);
        }
        
        // For other gemstones, we need weight and growth_origin
        return !!(selection.carat_weight || selection.measurement_value) &&
               selection.growth_origin !== undefined;
      });

      if (completeSelections.length > 0) {
        // Generate comment lines
        const commentLines = CommentLineGenerator.generateCommentLines(completeSelections);
        
        // Detect used abbreviations
        const usedAbbreviations = CommentLineGenerator.detectUsedAbbreviations(commentLines);
        
        // Generate abbreviation lines
        const abbreviationLines = AbbreviationLineGenerator.generateAbbreviationLines(usedAbbreviations);
        
        // Update form data (convert to uppercase)
        setFormData(prev => ({
          ...prev,
          comment_line_1: (commentLines[0] || '').toUpperCase(),
          comment_line_2: (commentLines[1] || '').toUpperCase(),
          comment_line_3: (commentLines[2] || '').toUpperCase(),
          abbreviation_line_1: (abbreviationLines[0] || '').toUpperCase(),
          abbreviation_line_2: (abbreviationLines[1] || '').toUpperCase(),
          abbreviation_line_3: (abbreviationLines[2] || '').toUpperCase(),
          abbreviation_line_4: (abbreviationLines[3] || '').toUpperCase(),
          abbreviation_line_5: (abbreviationLines[4] || '').toUpperCase(),
          abbreviation_line_6: (abbreviationLines[5] || '').toUpperCase(),
          abbreviation_line_7: (abbreviationLines[6] || '').toUpperCase(),
          abbreviation_line_8: (abbreviationLines[7] || '').toUpperCase(),
          abbreviation_line_9: (abbreviationLines[8] || '').toUpperCase(),
          abbreviation_line_10: (abbreviationLines[9] || '').toUpperCase(),
          abbreviation_line_11: (abbreviationLines[10] || '').toUpperCase(),
          abbreviation_line_12: (abbreviationLines[11] || '').toUpperCase(),
          abbreviation_line_13: (abbreviationLines[12] || '').toUpperCase()
        }));
        
        // Update field states
        setFieldStates(prev => ({
          ...prev,
          comment_line_1: { isAutoPopulated: !!commentLines[0], isEditable: !commentLines[0] },
          comment_line_2: { isAutoPopulated: !!commentLines[1], isEditable: !commentLines[1] },
          comment_line_3: { isAutoPopulated: !!commentLines[2], isEditable: !commentLines[2] },
          abbreviation_line_1: { isAutoPopulated: !!abbreviationLines[0], isEditable: !abbreviationLines[0] },
          abbreviation_line_2: { isAutoPopulated: !!abbreviationLines[1], isEditable: !abbreviationLines[1] },
          abbreviation_line_3: { isAutoPopulated: !!abbreviationLines[2], isEditable: !abbreviationLines[2] },
          abbreviation_line_4: { isAutoPopulated: !!abbreviationLines[3], isEditable: !abbreviationLines[3] },
          abbreviation_line_5: { isAutoPopulated: !!abbreviationLines[4], isEditable: !abbreviationLines[4] },
          abbreviation_line_6: { isAutoPopulated: !!abbreviationLines[5], isEditable: !abbreviationLines[5] },
          abbreviation_line_7: { isAutoPopulated: !!abbreviationLines[6], isEditable: !abbreviationLines[6] },
          abbreviation_line_8: { isAutoPopulated: !!abbreviationLines[7], isEditable: !abbreviationLines[7] },
          abbreviation_line_9: { isAutoPopulated: !!abbreviationLines[8], isEditable: !abbreviationLines[8] },
          abbreviation_line_10: { isAutoPopulated: !!abbreviationLines[9], isEditable: !abbreviationLines[9] },
          abbreviation_line_11: { isAutoPopulated: !!abbreviationLines[10], isEditable: !abbreviationLines[10] },
          abbreviation_line_12: { isAutoPopulated: !!abbreviationLines[11], isEditable: !abbreviationLines[11] },
          abbreviation_line_13: { isAutoPopulated: !!abbreviationLines[12], isEditable: !abbreviationLines[12] }
        }));
      }
    } else {
      // Reset field states when no gemstone selections
      setFieldStates(prev => ({
        comment_line_1: { isAutoPopulated: false, isEditable: true },
        comment_line_2: { isAutoPopulated: false, isEditable: true },
        comment_line_3: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_1: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_2: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_3: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_4: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_5: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_6: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_7: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_8: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_9: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_10: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_11: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_12: { isAutoPopulated: false, isEditable: true },
        abbreviation_line_13: { isAutoPopulated: false, isEditable: true }
      }));
    }
  }, [formData.gemstone_selections]);

  // Derived field validation for gemstone_types and gemstone_gross_weight
  useEffect(() => {
    if (formData.gemstone_selections && formData.gemstone_selections.length > 0) {
      // Calculate derived gemstone types
      const derivedGemstoneTypes = formData.gemstone_selections
        .filter(s => s.gemstone_type) // Only include selections with gemstone_type
        .map(s => s.gemstone_type);

      // Calculate derived gross weight from all gemstone selections
      const derivedGrossWeight = formData.gemstone_selections
        .reduce((total, s) => {
          // Use carat_weight first, then measurement_value as fallback
          const weight = parseFloat(s.carat_weight || s.measurement_value || '0');
          return total + (isNaN(weight) ? 0 : weight);
        }, 0)
        .toFixed(2);

      // Validate derived gemstone types
      const gemstoneTypesValidation = validationSchema.gemstone_types(derivedGemstoneTypes);
      
      // Validate derived gross weight
      const gemstoneGrossWeightValidation = validationSchema.gemstone_gross_weight(derivedGrossWeight);

      // Update validation states for derived fields
      setValidationState(prev => ({
        ...prev,
        gemstone_types: {
          ...gemstoneTypesValidation,
          isValid: gemstoneTypesValidation.valid,
          isChecking: false
        },
        gemstone_gross_weight: {
          ...gemstoneGrossWeightValidation,
          isValid: gemstoneGrossWeightValidation.valid,
          isChecking: false
        }
      }));

      console.log('🔍 Derived field validation updated:', {
        gemstoneTypes: derivedGemstoneTypes,
        grossWeight: derivedGrossWeight,
        typesValid: gemstoneTypesValidation.valid,
        weightValid: gemstoneGrossWeightValidation.valid
      });
    } else {
      // Reset validation states when no gemstone selections
      setValidationState(prev => ({
        ...prev,
        gemstone_types: {
          valid: false,
          message: 'At least one gemstone type is required',
          code: 'GEM_001',
          isValid: false,
          isChecking: false
        },
        gemstone_gross_weight: {
          valid: false,
          message: 'Gemstone gross weight is required',
          code: 'GEM_003',
          isValid: false,
          isChecking: false
        }
      }));

      console.log('🔍 Derived field validation reset - no gemstone selections');
    }
  }, [formData.gemstone_selections]);

  // Handle image file selection
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Clear the input value to allow selecting the same file again
    event.target.value = ''

    // Validate file type (accept JPEG, PNG, WebP for cropping)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      addStatusMessage('Invalid file type. Only JPEG, PNG, and WebP images are allowed.', 'error')
      return
    }

    // ✅ NO SIZE CHECK - Allow large files for cropping!
    // Size will be enforced AFTER crop + WebP conversion

    // Warn if file is very large (>5MB) - might be slow
    if (file.size > 5000000) {
      addStatusMessage('⚠️ Large file detected. Processing may take a moment...', 'warning')
    }

    // Open crop modal (MANDATORY)
    setSelectedFileForCrop(file)
    setShowCropModal(true)
    console.log('🖼️ Opening crop modal for:', file.name, `(${Math.round(file.size / 1024)}KB)`)
  }

  // Handle staging image selection (bypass crop modal)
  const handleStagingImageSelected = async (filename: string, imageBlob: Blob) => {
    try {
      addStatusMessage('🔄 Processing image from staging...', 'info')
      console.log(`🗂️ Processing staging image: ${filename}`)
      
      // Convert to WebP and compress
      const webpBlob = await convertBlobToWebP(imageBlob, 500)
      
      // Create File object
      const webpFilename = filename.replace(/\.(jpg|jpeg|png)$/i, '.webp')
      const webpFile = new File([webpBlob], webpFilename, { type: 'image/webp' })
      
      // Check size
      if (webpFile.size > 512000) {
        addStatusMessage(
          `❌ Converted image is ${Math.round(webpFile.size / 1024)}KB (max 500KB). Please choose a different image.`,
          'error'
        )
        return
      }
      
      // Set in imageUpload state (bypass crop modal)
      setImageUpload({
        file: webpFile,
        selected: true,
        uploaded: false,
        uploading: false,
        presignedUrl: null,
        originalFilename: filename,
        systemFilename: null,
        s3Key: null,
        uploadId: null
      })
      
      // Switch back to computer mode to show regular upload UI
      setUploadMethod('computer')
      
      addStatusMessage(`✅ Image loaded from staging: ${webpFilename} (${Math.round(webpFile.size / 1024)}KB)`, 'success')
      console.log('✅ Staging image ready for upload')
      
    } catch (error) {
      console.error('❌ Failed to process staging image:', error)
      addStatusMessage(`Failed to process staging image: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    }
  }

  // Helper: Convert blob to WebP with compression
  const convertBlobToWebP = async (blob: Blob, maxSizeKB: number): Promise<Blob> => {
    return new Promise(async (resolve, reject) => {
      try {
        const img = await createImageBitmap(blob)
        
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas context not available')
        
        ctx.drawImage(img, 0, 0)
        
        // Try decreasing quality until size is acceptable
        let quality = 0.85
        
        const tryCompress = () => {
          canvas.toBlob((webpBlob) => {
            if (!webpBlob) {
              reject(new Error('WebP conversion failed'))
              return
            }
            
            const sizeKB = webpBlob.size / 1024
            
            if (sizeKB <= maxSizeKB) {
              resolve(webpBlob)
            } else if (quality > 0.1) {
              quality -= 0.05
              tryCompress()
            } else {
              reject(new Error(`Cannot compress image below ${maxSizeKB}KB`))
            }
          }, 'image/webp', quality)
        }
        
        tryCompress()
        
      } catch (error) {
        reject(error)
      }
    })
  }

  // Handle crop completion
  const handleCropComplete = (croppedWebPFile: File) => {
    setShowCropModal(false)
    setSelectedFileForCrop(null)

    // ✅ NOW check size AFTER processing
    if (croppedWebPFile.size > 512000) {
      addStatusMessage(
        `❌ Processed image is ${Math.round(croppedWebPFile.size / 1024)}KB (max 500KB). Try cropping closer or adjusting quality.`,
        'error'
      )
      return
    }

    // Success! Set the cropped file
    setImageUpload(prev => ({
      ...prev,
      file: croppedWebPFile,
      selected: true,
      uploaded: false,
      uploading: false
    }))

    addStatusMessage(
      `✅ Image ready! Size: ${Math.round(croppedWebPFile.size / 1024)}KB. Click "Upload Image" to continue.`,
      'success'
    )
    console.log('✅ Cropped WebP ready:', croppedWebPFile.name, `(${croppedWebPFile.size} bytes)`)
  }

  // Handle crop cancel
  const handleCropCancel = () => {
    setShowCropModal(false)
    setSelectedFileForCrop(null)
    addStatusMessage('Image selection cancelled.', 'info')
  }

  // Handle actual upload to S3
  const handleImageUpload = async () => {
    if (!imageUpload.file) {
      addStatusMessage('No image selected for upload.', 'error')
      return
    }

    // Show re-authentication modal for security
    setShowReAuthModal(true)
  }

  // Actual image upload after re-authentication
  const performImageUpload = async (freshToken: string) => {
    if (!imageUpload.file) {
      addStatusMessage('No image selected for upload.', 'error')
      return
    }

    // Validate certificate ID before upload
    if (!formData.cert_id || formData.cert_id.length < 3) {
      addStatusMessage('Please enter a valid Certificate ID before uploading image.', 'error')
      return
    }

    // Check if certificate ID validation has passed
    if (!validationState.cert_id?.isValid) {
      addStatusMessage('Certificate ID must be valid and available before uploading image.', 'error')
      return
    }

    const file = imageUpload.file
    const isReplacement = imageUpload.uploaded

    setImageUpload(prev => ({ ...prev, uploading: true }))
    
    if (isReplacement) {
      addStatusMessage('Replacing existing image...', 'info')
    } else {
      addStatusMessage('Uploading image to server...', 'info')
    }

    try {
      // Use proxy upload to avoid CORS issues
      const formDataToSend = new FormData()
      formDataToSend.append('file', file)
      formDataToSend.append('certificateId', formData.cert_id)
      formDataToSend.append('cardDesignCode', formData.card_design_code)

      console.log('🔍 Performing image upload with fresh token')
      console.log('🔍 Fresh token:', freshToken ? 'Present' : 'Missing')
      console.log('🔍 User ID:', user?.id || 'current-user-id')
      
      // 30-second timeout to prevent UI getting stuck if backend never responds
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000)

      let response: Response
      try {
        response = await fetch('/api/s3/proxy-upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${freshToken}`,
            'X-User-ID': user?.id || 'current-user-id'
          },
          body: formDataToSend,
          signal: controller.signal
        })
      } finally {
        clearTimeout(timeoutId)
      }

      console.log('🔍 Upload response status:', response.status)
      console.log('🔍 Upload response ok:', response.ok)
      
      const result = await response.json()
      console.log('🔍 Upload result:', result)

      if (result.success) {
        setImageUpload(prev => ({ 
          ...prev, 
          uploaded: true, 
          uploading: false,
          originalFilename: result.data.originalFilename,
          systemFilename: result.data.systemFilename,
          s3Key: result.data.s3Key,
          uploadId: result.data.uploadId
        }))
        setFieldsLocked(true) // Lock all fields after successful upload
        const message = isReplacement 
          ? 'Image replaced successfully! All fields are now locked.'
          : 'Image uploaded successfully! All fields are now locked.'
        addStatusMessage(message, 'success')
        console.log('🔍 Fields locked after successful upload:', true)
      } else {
        throw new Error(result.message)
      }
    } catch (error: any) {
      console.error('Image upload error:', error)
      const isAbort = error?.name === 'AbortError'
      const msg = isAbort
        ? 'Upload timed out. Please try again.'
        : `Image upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      addStatusMessage(msg, 'error')
      setImageUpload(prev => ({ ...prev, uploading: false }))
    }
  }

  // Re-authentication modal handlers
  const handleReAuthSuccess = async (freshToken: string) => {
    setShowReAuthModal(false)
    await performImageUpload(freshToken)
  }

  const handleReAuthCancel = () => {
    setShowReAuthModal(false)
    addStatusMessage('Image upload cancelled.', 'info')
  }

  const handleReAuthRedirect = () => {
    setShowReAuthModal(false)
    addStatusMessage('Redirecting to login page...', 'info')
    setTimeout(() => {
      window.location.href = '/login'
    }, 1000)
  }

  // Session extension modal handlers
  const handleSessionExtend = async (password: string) => {
    try {
      const result = await SessionManager.extendSession(user?.username || '', password)
      if (result.success) {
        // Success is handled by the SessionManager callback
        return
      } else {
        throw new Error(result.message || 'Extension failed')
      }
    } catch (error) {
      throw error // Re-throw to be handled by the modal
    }
  }

  const handleSessionExtensionCancel = () => {
    setShowSessionExtension(false)
    // Keep the warning visible but hide the modal
  }

  const handleSessionLogout = () => {
    setShowSessionExtension(false)
    addStatusMessage('Logging out...', 'info')
    setTimeout(() => {
      window.location.href = '/login'
    }, 1000)
  }

  // Remove image (works for both preview and uploaded images)
  const handleRemoveImage = async () => {
    try {
      // If image is uploaded to S3, remove it from S3
      if (imageUpload.uploaded && imageUpload.systemFilename) {
        const response = await fetch(`/api/s3/remove-image/${formData.cert_id}`, {
          method: 'DELETE',
          headers: {
            'X-User-ID': user?.id || 'current-user-id'
          }
        })

        const result = await response.json()

        if (!result.success) {
          console.warn('Failed to remove from S3:', result.message)
          // Continue anyway to clear the local state
        }
      }

      // Clear the image state (works for both preview and uploaded)
      setImageUpload({
        file: null,
        selected: false,
        uploaded: false,
        uploading: false,
        presignedUrl: null,
        originalFilename: null,
        systemFilename: null,
        s3Key: null,
        uploadId: null
      })
      
      // Only unlock fields if image was actually uploaded
      if (imageUpload.uploaded) {
        setFieldsLocked(false)
        addStatusMessage('Image removed successfully. Fields are now unlocked for editing.', 'info')
      } else {
        addStatusMessage('Image removed from preview.', 'info')
      }
    } catch (error) {
      addStatusMessage(`Failed to remove image: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    }
  }

  // Handle form submission - show verification checkpoint
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    console.log('🔍 Form submit triggered')
    console.log('🔍 Form data:', formData)
    console.log('🔍 User:', user)
    
    // Check if form can be submitted (no critical errors)
    if (!canSubmitForm()) {
      console.log('❌ Form has critical errors, cannot submit')
      return
    }
    
    // Show verification checkpoint instead of direct submission
    setShowVerificationCheckpoint(true)
  }

  // Handle verification checkpoint field edit
  const handleVerificationFieldEdit = (field: string, newValue: string) => {
    console.log('🔍 Verification field edit:', field, newValue)
    
    // Handle different field types
    if (field.startsWith('weight_')) {
      const gemstoneType = field.replace('weight_', '')
      setFormData(prev => ({
        ...prev,
        gemstone_selections: prev.gemstone_selections.map(s =>
          s.gemstone_type === gemstoneType ? { ...s, carat_weight: newValue } : s
        )
      }))
    } else if (field === 'cert_id') {
      // Filter to only allow A-Z and 0-9, then convert to uppercase
      const filteredValue = newValue.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
      setFormData(prev => ({ ...prev, cert_id: filteredValue }))
    } else if (field === 'cert_id_confirm') {
      // Filter to only allow A-Z and 0-9, then convert to uppercase
      const filteredValue = newValue.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
      setFormData(prev => ({ ...prev, cert_id_confirm: filteredValue }))
    } else if (field === 'metal_composition') {
      setFormData(prev => ({ ...prev, metal_composition: newValue as any }))
    } else if (field === 'item_gross_weight') {
      setFormData(prev => ({ ...prev, item_gross_weight: newValue }))
    } else if (field === 'card_design_code') {
      setFormData(prev => ({ ...prev, card_design_code: newValue }))
    } else if (field.startsWith('comment_line_')) {
      const lineNumber = field.replace('comment_line_', '')
      setFormData(prev => ({ ...prev, [`comment_line_${lineNumber}`]: newValue.toUpperCase() }))
    } else if (field.startsWith('abbreviation_line_')) {
      const lineNumber = field.replace('abbreviation_line_', '')
      setFormData(prev => ({ ...prev, [`abbreviation_line_${lineNumber}`]: newValue.toUpperCase() }))
    }
  }

  // Handle final certificate submission after verification
  const handleVerifiedSubmit = async () => {
    setIsSubmitting(true)
    
    try {
      console.log('🔍 Submitting verified certificate...')
      
      const apiData = {
        cert_id: formData.cert_id,
        gemstone_selections: formData.gemstone_selections.map(s => ({
          gemstone_type: s.gemstone_type,
          carat_weight: s.carat_weight // Keep as string for API
        })),
        metal_composition: formData.metal_composition,
        item_gross_weight: formData.item_gross_weight,
        gemstone_growth_origin: formData.gemstone_growth_origin,
        card_design_code: formData.card_design_code,
        // Image data from S3 upload
        image_url: imageUpload.uploaded && imageUpload.s3Key ? 
          `https://verifygold.s3.me-central-1.amazonaws.com/${imageUpload.s3Key}` : null,
        image_filename: imageUpload.systemFilename,
        image_size_bytes: imageUpload.file?.size || null,
        comment_line_1: formData.comment_line_1,
        comment_line_2: formData.comment_line_2,
        comment_line_3: formData.comment_line_3,
        abbreviation_line_1: formData.abbreviation_line_1,
        abbreviation_line_2: formData.abbreviation_line_2,
        abbreviation_line_3: formData.abbreviation_line_3,
        abbreviation_line_4: formData.abbreviation_line_4,
        abbreviation_line_5: formData.abbreviation_line_5,
        abbreviation_line_6: formData.abbreviation_line_6,
        abbreviation_line_7: formData.abbreviation_line_7,
        abbreviation_line_8: formData.abbreviation_line_8,
        abbreviation_line_9: formData.abbreviation_line_9,
        abbreviation_line_10: formData.abbreviation_line_10,
        abbreviation_line_11: formData.abbreviation_line_11,
        abbreviation_line_12: formData.abbreviation_line_12,
        abbreviation_line_13: formData.abbreviation_line_13
      }

      console.log('🔍 API Data:', apiData)

      // TODO: Get auth token from auth context
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('http://localhost:3000/api/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiData)
      })

      const result = await response.json()
      console.log('🔍 API Response:', result)

      if (result.success) {
        console.log('✅ Certificate created successfully!')
        setCertificateUuid(result.data.certificate.id)
        setShowVerificationCheckpoint(false)
        // Reset form or redirect as needed
      } else {
        console.error('❌ Certificate creation failed:', result.message)
        // Handle error
      }
    } catch (error) {
      console.error('❌ Certificate submission error:', error)
      // Handle error
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle verification checkpoint cancel
  const handleVerificationCancel = () => {
    setShowVerificationCheckpoint(false)
  }

  // Save certificate
  const handleSaveCertificate = async () => {
    if (!imageUpload.uploaded) {
      addStatusMessage('Please upload an image before saving the certificate.', 'error')
      return
    }

    // Validate token before proceeding
    const tokenValidation = await TokenManager.validateTokenForApiCall()
    console.log('🔍 Token validation result:', tokenValidation)
    if (!tokenValidation.valid) {
      console.log('🔍 Token validation failed - DEBUGGING MODE')
      addStatusMessage(tokenValidation.message || 'Session expired. Please log in again.', 'error')
      // TEMPORARILY DISABLED FOR DEBUGGING
      console.log('🔍 Token validation redirect TEMPORARILY DISABLED for debugging')
      // if (tokenValidation.message?.includes('expired') || tokenValidation.message?.includes('No authentication')) {
      //   setTimeout(() => {
      //     window.location.href = '/login'
      //   }, 2000)
      // }
      return
    }

    console.log('🔍 Save Certificate clicked')
    console.log('🔍 Image upload state:', imageUpload)
    console.log('🔍 Form data:', formData)

    try {
      // Prepare API data - keep as strings, service will handle conversion
      const apiData: CertificateFormData = {
        cert_id: formData.cert_id,
        gemstone_selections: formData.gemstone_selections.map(s => {
          // Look up IDs from reference data
          const gemstoneType = referenceData.gemstone_types.find(gt => gt.gemstone_type === s.gemstone_type);
          const growthOrigin = s.growth_origin ? referenceData.growth_origins.find(go => go.growth_origin === s.growth_origin) : null;
          
          return {
            gemstone_type: s.gemstone_type,
            gemstone_type_id: gemstoneType?.id,
            // Pearls must have null growth_origin, others must have a value
            growth_origin: s.gemstone_type?.includes('PEARL') ? null : s.growth_origin,
            growth_origin_id: s.gemstone_type?.includes('PEARL') ? null : growthOrigin?.id,
            measurement_type: s.measurement_type,
            measurement_value: s.measurement_value,
            measurement_range: s.measurement_range,
            measurement_unit: s.measurement_unit,
            carat_weight: formatWeightValue(s.carat_weight || ''), // Keep as string
            // Diamond-specific fields
            diamond_color_type: s.diamond_color_type,
            diamond_color_fixed: s.diamond_color_fixed,
            diamond_color_range: s.diamond_color_range,
            diamond_clarity_type: s.diamond_clarity_type,
            diamond_clarity_fixed: s.diamond_clarity_fixed,
            diamond_clarity_range: s.diamond_clarity_range
          };
        }),
        metal_composition: formData.metal_composition,
        item_gross_weight: formatWeightValue(formData.item_gross_weight), // Keep as string
        gemstone_growth_origin: formData.gemstone_growth_origin,
        card_design_code: formData.card_design_code,
        // Image data from S3 upload
        image_url: imageUpload.uploaded && imageUpload.s3Key ? 
          `https://verifygold.s3.me-central-1.amazonaws.com/${imageUpload.s3Key}` : null,
        image_filename: imageUpload.systemFilename,
        image_size_bytes: imageUpload.file?.size || null,
        original_filename: imageUpload.originalFilename,
        comment_line_1: formData.comment_line_1,
        comment_line_2: formData.comment_line_2,
        comment_line_3: formData.comment_line_3,
        abbreviation_line_1: formData.abbreviation_line_1,
        abbreviation_line_2: formData.abbreviation_line_2,
        abbreviation_line_3: formData.abbreviation_line_3,
        abbreviation_line_4: formData.abbreviation_line_4,
        abbreviation_line_5: formData.abbreviation_line_5,
        abbreviation_line_6: formData.abbreviation_line_6,
        abbreviation_line_7: formData.abbreviation_line_7,
        abbreviation_line_8: formData.abbreviation_line_8,
        abbreviation_line_9: formData.abbreviation_line_9,
        abbreviation_line_10: formData.abbreviation_line_10,
        abbreviation_line_11: formData.abbreviation_line_11,
        abbreviation_line_12: formData.abbreviation_line_12,
        abbreviation_line_13: formData.abbreviation_line_13
      }

      // Get fresh auth token using TokenManager (updated by ReAuthModal)
      const authToken = TokenManager.getToken() || 'mock-token'
      
      console.log('🔍 API Data being sent:', apiData)
      console.log('🔍 Auth token:', authToken)
      
      const result = await certificateService.createCertificate(apiData, authToken)
      
      console.log('🔍 API Response:', result)

      if (result.success && result.data) {
        setCertificateCreated(true)
        setCertificateUuid((result.data as any).certificate?.uurlid || (result.data as any).uurlid || null)
        addStatusMessage('Certificate created successfully!', 'success')
        console.log('🔍 Certificate created successfully:', result.data)
      } else {
        console.error('🔍 Certificate creation failed:', result)
        
        // Extract error code and message from API response with better fallbacks
        let errorCode = 'VALIDATION_ERROR'
        let errorMessage = result.message || 'Failed to create certificate'
        
        // Try to extract error code from different possible locations
        if ((result as any).code) {
          errorCode = (result as any).code
        } else if ((result as any).error?.code) {
          errorCode = (result as any).error.code
        } else if ((result as any).data?.code) {
          errorCode = (result as any).data.code
        } else if (errorMessage.includes('PEARL') && errorMessage.includes('measurement')) {
          errorCode = 'PEARL_MEASUREMENT_REQUIRED'
        } else if (errorMessage.includes('required')) {
          errorCode = 'REQUIRED_FIELD_MISSING'
        }
        
        // Display error with code
        const displayMessage = `Failed to create certificate: ${errorMessage} (Code: ${errorCode})`
        addStatusMessage(displayMessage, 'error', errorCode)
        return // Don't throw, just return after showing the error
      }
    } catch (error) {
      console.error('🔍 Certificate creation error:', error)
      addStatusMessage(`Failed to create certificate: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    }
  }

  // Add status message
  const addStatusMessage = (message: string, type: StatusMessage['type'] = 'info', code?: string) => {
    const newMessage: StatusMessage = {
      type,
      message,
      code,
      timestamp: Date.now()
    }
    setStatusMessages(prev => [newMessage, ...prev.slice(0, 4)])
  }

  // Open public certificate
  const openPublicCertificate = () => {
    if (certificateUuid) {
      window.open(`/${certificateUuid}`, '_blank')
    }
  }

  // Handle logout
  const handleLogout = () => {
    // Confirm logout action
    if (window.confirm('Are you sure you want to logout?')) {
      logout()
    }
  }

  // Handle return to dashboard
  const handleReturnToDashboard = () => {
    // Navigate to dashboard using the router
    navigate('/dashboard')
  }

  // Handle creating a new certificate (reset form)
  const handleCreateNewCertificate = () => {
    // Reset all form state to initial values
    setFormData({
      cert_id: '',
      cert_id_confirm: '',
      gemstone_selections: [],
      metal_composition: '18K WHITE GOLD',
      item_gross_weight: '',
      gemstone_growth_origin: 'NATURAL',
      card_design_code: 'EUROPEANGEMOLOGICALLAB',
      comment_line_1: '',
      comment_line_2: '',
      comment_line_3: '',
      abbreviation_line_1: '',
      abbreviation_line_2: '',
      abbreviation_line_3: '',
      abbreviation_line_4: '',
      abbreviation_line_5: '',
      abbreviation_line_6: '',
      abbreviation_line_7: '',
      abbreviation_line_8: '',
      abbreviation_line_9: '',
      abbreviation_line_10: '',
      abbreviation_line_11: '',
      abbreviation_line_12: '',
      abbreviation_line_13: ''
    })

    // Reset field states
    setFieldStates({
      comment_line_1: { isAutoPopulated: false, isEditable: true },
      comment_line_2: { isAutoPopulated: false, isEditable: true },
      comment_line_3: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_1: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_2: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_3: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_4: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_5: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_6: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_7: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_8: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_9: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_10: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_11: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_12: { isAutoPopulated: false, isEditable: true },
      abbreviation_line_13: { isAutoPopulated: false, isEditable: true }
    })

    // Reset validation state
    setValidationState({
      cert_id: { isValid: false, isChecking: false, message: '', code: '' },
      gemstone_types: { isValid: false, isChecking: false, message: '', code: '' },
      gemstone_gross_weight: { isValid: false, isChecking: false, message: '', code: '' },
      item_gross_weight: { isValid: false, isChecking: false, message: '', code: '' }
    })

    // Reset image upload state
    setImageUpload({
      file: null,
      selected: false,
      uploaded: false,
      uploading: false,
      presignedUrl: null,
      originalFilename: null,
      systemFilename: null,
      s3Key: null,
      uploadId: null
    })

    // Reset other states
    setCertificateCreated(false)
    setCertificateUuid(null)
    setShowVerificationCheckpoint(false)
    setFieldsLocked(false)

    // Clear status messages
    setStatusMessages([])

    // Add success message
    addStatusMessage('Ready to create a new certificate!', 'success')

    // Scroll to top - try multiple methods for better compatibility
    // First try to scroll the form container
    const formContainer = document.querySelector('.creation-form-side')
    if (formContainer) {
      formContainer.scrollTo({ top: 0, behavior: 'smooth' })
    }
    
    // Also scroll the main window as fallback
    window.scrollTo({ top: 0, behavior: 'smooth' })
    
    // Additional fallback for older browsers
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 100)
  }

  // Smart gemstone display formatting (space-aware)
  const formatGemstonesForDisplay = (gemstones: Array<{gemstone_type: string, abbreviation: string}>, maxLength = 50) => {
    if (!gemstones || gemstones.length === 0) return '';
    
    // Try full names first
    const fullNames = gemstones.map(g => g.gemstone_type).join(', ');
    if (fullNames.length <= maxLength) {
      return fullNames;
    }
    
    // Fall back to abbreviations
    const abbreviations = gemstones.map(g => g.abbreviation).join(', ');
    if (abbreviations.length <= maxLength) {
      return abbreviations;
    }
    
    // Ultra-compact: just abbreviations without spaces
    return gemstones.map(g => g.abbreviation).join(',');
  };

  // Format for different display contexts
  const formatForContext = (gemstones: Array<{gemstone_type: string, abbreviation: string}>, context: 'form' | 'card' | 'print' | 'compact') => {
    const limits = {
      form: 100,      // Form display - plenty of space
      card: 40,       // Certificate card display
      print: 25,      // Print job constraints
      compact: 15     // Very limited space
    };
    
    return formatGemstonesForDisplay(gemstones, limits[context]);
  };

  // Display selected gemstones with smart formatting
  const displaySelectedGemstones = () => {
    const selectedGemstones = referenceData.gemstone_types.filter(g => 
      formData.gemstone_selections.some(s => s.gemstone_type === g.gemstone_type)
    );
    
    return formatForContext(selectedGemstones, 'form');
  };

  // Enhanced gemstone weight change handler - allow complete clearing
  const handleGemstoneWeightChange = (gemstoneType: string, weight: string) => {
    if (fieldsLocked) {
      console.log('🔒 Gemstone weight change blocked - form is locked');
      return;
    }
    console.log('🔍 Gemstone weight change:', gemstoneType, weight);
    
    // Always allow the change - including complete clearing
    setFormData(prev => ({
      ...prev,
      gemstone_selections: prev.gemstone_selections.map(s =>
        s.gemstone_type === gemstoneType ? { 
          ...s, 
          carat_weight: weight,
          measurement_value: weight // Keep both for compatibility
        } : s
      )
    }));
    
    // Log the change
    if (weight === '') {
      console.log('✅ Field cleared for', gemstoneType);
    } else {
      console.log('✅ Weight input accepted:', weight);
    }
  };

  // Gemstone weight blur handler - format valid values only, preserve empty fields
  const handleGemstoneWeightBlur = (gemstoneType: string, weight: string) => {
    console.log(`🔍 Gemstone weight blur ${gemstoneType}: "${weight}"`);
    
    // If field is empty, leave it empty - don't auto-fill
    if (!weight || weight.trim() === '') {
      console.log('✅ Field left empty for validation to handle');
      return;
    }
    
    // Only format if there's a valid positive number
    const num = parseFloat(weight);
    if (!isNaN(num) && num > 0) {
      const finalWeight = formatWeightValue(weight);
      setFormData(prev => ({
        ...prev,
        gemstone_selections: prev.gemstone_selections.map(s =>
          s.gemstone_type === gemstoneType ? { 
            ...s, 
            carat_weight: finalWeight,
            measurement_value: finalWeight 
          } : s
        )
      }));
      console.log(`✅ Formatted weight: ${weight} → ${finalWeight}`);
    } else {
      console.log('⚠️ Invalid weight, leaving as-is for validation');
    }
  };

  // Handle pearl diameter range change with separate min/max fields
  const handlePearlRangeMinChange = (gemstoneType: string, minValue: string) => {
    if (fieldsLocked) {
      console.log('🔒 Pearl range min change blocked - form is locked');
      return;
    }
    console.log('🔍 Pearl range min change:', gemstoneType, minValue);
    
    setFormData(prev => ({
      ...prev,
      gemstone_selections: prev.gemstone_selections.map(s => {
        if (s.gemstone_type === gemstoneType) {
          const currentRange = s.measurement_range || '-';
          const maxValue = currentRange.split('-')[1] || '';
          const newRange = `${minValue}-${maxValue}`;
          
          return { 
            ...s, 
            measurement_range: newRange,
            measurement_type: 'DIAMETER_RANGE',
            measurement_value: undefined
          };
        }
        return s;
      })
    }));
  };

  const handlePearlRangeMaxChange = (gemstoneType: string, maxValue: string) => {
    if (fieldsLocked) {
      console.log('🔒 Pearl range max change blocked - form is locked');
      return;
    }
    console.log('🔍 Pearl range max change:', gemstoneType, maxValue);
    
    setFormData(prev => ({
      ...prev,
      gemstone_selections: prev.gemstone_selections.map(s => {
        if (s.gemstone_type === gemstoneType) {
          const currentRange = s.measurement_range || '-';
          const minValue = currentRange.split('-')[0] || '';
          const newRange = `${minValue}-${maxValue}`;
          
          return { 
            ...s, 
            measurement_range: newRange,
            measurement_type: 'DIAMETER_RANGE',
            measurement_value: undefined
          };
        }
        return s;
      })
    }));
  };

  // Handle pearl single diameter change
  const handlePearlDiameterChange = (gemstoneType: string, diameter: string) => {
    if (fieldsLocked) {
      console.log('🔒 Pearl diameter change blocked - form is locked');
      return;
    }
    console.log('🔍 Pearl diameter change:', gemstoneType, diameter);
    
    setFormData(prev => ({
      ...prev,
      gemstone_selections: prev.gemstone_selections.map(s =>
        s.gemstone_type === gemstoneType ? { 
          ...s, 
          measurement_value: diameter,
          measurement_type: 'DIAMETER',
          measurement_range: undefined // Clear range when using single value
        } : s
      )
    }));
  };

  // Fetch diamond reference data from database
  const fetchDiamondReferenceData = async () => {
    try {
      console.log('🔍 Fetching diamond reference data...')
      const response = await fetch('/api/certificates/public/diamond-reference-data')
      const data = await response.json()
      
      if (data.success) {
        console.log('✅ Diamond reference data loaded:', data.data)
        // Map backend response to frontend structure
        setDiamondReferenceData({
          diamond_colors: data.data.colors || [],
          diamond_clarity: data.data.clarity || [],
          diamond_color_ranges: data.data.color_ranges || [],
          diamond_clarity_ranges: data.data.clarity_ranges || [],
          defaults: data.data.defaults || {color_range: 'G-H', clarity_range: 'VS-SI'}
        })
      } else {
        console.error('❌ Failed to fetch diamond reference data:', data.message)
      }
    } catch (error) {
      console.error('❌ Error fetching diamond reference data:', error)
    }
  }

  // Auto-calculate certificate-level growth origin based on individual gemstone origins
  const autoSelectCertificateGrowthOrigin = (selections: any[]): 'NATURAL' | 'LAB GROWN' | 'NATURAL & LAB GROWN' => {
    // Filter out pearls (they don't have growth origin)
    const gemstonesWithOrigin = selections.filter(s => !s.gemstone_type.includes('PEARL') && s.growth_origin);
    
    if (gemstonesWithOrigin.length === 0) {
      return 'NATURAL'; // Default fallback
    }
    
    const origins = gemstonesWithOrigin.map(s => s.growth_origin);
    const hasNatural = origins.includes('NATURAL');
    const hasLabGrown = origins.includes('LAB GROWN');
    
    if (hasNatural && hasLabGrown) {
      return 'NATURAL & LAB GROWN';
    } else if (hasLabGrown) {
      return 'LAB GROWN';
    } else {
      return 'NATURAL';
    }
  };

  // Generate gemstone summary for display
  const generateGemstoneSummary = (): Array<{type: string, name: string, details: Array<{text: string, missing: boolean}>, caratWeight: number}> => {
    const selections = formData.gemstone_selections.filter(s => s.gemstone_type);
    if (selections.length === 0) return [];

    // Separate diamonds and other gemstones
    const diamonds = selections.filter(s => s.gemstone_type.startsWith('DIAMOND_')).sort((a, b) => a.gemstone_type.localeCompare(b.gemstone_type));
    const otherGemstones = selections.filter(s => !s.gemstone_type.startsWith('DIAMOND_')).sort((a, b) => a.gemstone_type.localeCompare(b.gemstone_type));

    const summary: Array<{type: string, name: string, details: Array<{text: string, missing: boolean}>, caratWeight: number}> = [];

    // Add diamonds first
    diamonds.forEach(diamond => {
      const diamondNumber = diamond.gemstone_type.replace('DIAMOND_', '');
      
      const colorValue = diamond.diamond_color_type === 'FIXED' 
        ? diamond.diamond_color_fixed 
        : diamond.diamond_color_range;
      const colorLabel = diamond.diamond_color_type === 'FIXED' ? 'Color' : 'Color Range';
      const colorInfo = `${colorLabel}: ${colorValue || 'Not selected'}`;
      const colorMissing = !colorValue;
      
      const clarityValue = diamond.diamond_clarity_type === 'FIXED'
        ? diamond.diamond_clarity_fixed
        : diamond.diamond_clarity_range;
      const clarityLabel = diamond.diamond_clarity_type === 'FIXED' ? 'Clarity' : 'Clarity Range';
      const clarityInfo = `${clarityLabel}: ${clarityValue || 'Not selected'}`;
      const clarityMissing = !clarityValue;

      summary.push({
        type: 'diamond',
        name: `Diamond ${diamondNumber}`,
        details: [
          { text: colorInfo, missing: colorMissing },
          { text: clarityInfo, missing: clarityMissing },
          { text: `Growth Origin: ${diamond.growth_origin || 'Not selected'}`, missing: !diamond.growth_origin },
          { text: `Carats: ${diamond.carat_weight || 'Not entered'}`, missing: !diamond.carat_weight }
        ],
        caratWeight: parseFloat(diamond.carat_weight || '0')
      });
    });

    // Add other gemstones
    otherGemstones.forEach(gemstone => {
      const details: Array<{text: string, missing: boolean}> = [];
      
      if (gemstone.gemstone_type.includes('PEARL')) {
        // Pearl handling with validation
        if (gemstone.measurement_type === 'DIAMETER_RANGE') {
          const rangeValue = gemstone.measurement_range || '';
          const validation = getValidationMessage(rangeValue, gemstone.gemstone_type);
          const hasError = !validation.allowSubmit && rangeValue !== '';
          
          details.push({ 
            text: hasError 
              ? `Diameter Range: ${rangeValue} mm - ${validation.message}`
              : `Diameter Range: ${rangeValue || 'Not entered'} mm`, 
            missing: !rangeValue || hasError
          });
        } else {
          const diameterValue = gemstone.measurement_value || '';
          const validation = getValidationMessage(diameterValue, gemstone.gemstone_type);
          const hasError = !validation.allowSubmit && diameterValue !== '';
          
          details.push({ 
            text: hasError
              ? `Diameter: ${diameterValue} mm - ${validation.message}`
              : `Diameter: ${diameterValue || 'Not entered'} mm`, 
            missing: !diameterValue || hasError
          });
        }
      } else {
        // Regular gemstone handling
        details.push({ 
          text: `Growth Origin: ${gemstone.growth_origin || 'Not selected'}`, 
          missing: !gemstone.growth_origin 
        });
        details.push({ 
          text: `Carats: ${gemstone.carat_weight || 'Not entered'}`, 
          missing: !gemstone.carat_weight 
        });
      }

      summary.push({
        type: 'gemstone',
        name: gemstone.gemstone_type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
        details,
        caratWeight: gemstone.gemstone_type.includes('PEARL') ? 0 : parseFloat(gemstone.carat_weight || '0')
      });
    });

    return summary;
  };

  // Calculate diamond running total
  const calculateDiamondTotal = () => {
    const diamonds = formData.gemstone_selections.filter(s => s.gemstone_type.startsWith('DIAMOND_'));
    return diamonds.reduce((total, diamond) => total + parseFloat(diamond.carat_weight || '0'), 0).toFixed(2);
  };

  // Calculate total gemstone gross weight (all gemstones combined)
  const calculateGemstoneTotalWeight = () => {
    const allGemstones = formData.gemstone_selections.filter(s => s.gemstone_type && !s.gemstone_type.includes('PEARL'));
    return allGemstones.reduce((total, gemstone) => total + parseFloat(gemstone.carat_weight || '0'), 0).toFixed(2);
  };

  // Check if a diamond can be selected based on sequential selection rules
  const canSelectDiamond = (diamondType: string): boolean => {
    if (diamondType === 'DIAMOND_1') {
      return true; // Diamond 1 can always be selected
    }
    
    if (diamondType === 'DIAMOND_2') {
      // Diamond 2 can only be selected if Diamond 1 is already selected
      return formData.gemstone_selections.some(s => s.gemstone_type === 'DIAMOND_1');
    }
    
    if (diamondType === 'DIAMOND_3') {
      // Diamond 3 can only be selected if Diamond 2 is already selected
      return formData.gemstone_selections.some(s => s.gemstone_type === 'DIAMOND_2');
    }
    
    return true; // Non-diamond gemstones can always be selected
  };

  // Handle individual gemstone growth origin change
  const handleIndividualGrowthOriginChange = (gemstoneType: string, growthOrigin: 'NATURAL' | 'LAB GROWN') => {
    if (fieldsLocked) {
      console.log('🔒 Growth origin change blocked - form is locked');
      return;
    }
    console.log('🔍 Individual growth origin change:', gemstoneType, growthOrigin);
    
    setFormData(prev => {
      const newSelections = prev.gemstone_selections.map(s =>
        s.gemstone_type === gemstoneType ? { 
          ...s, 
          growth_origin: growthOrigin
        } : s
      );
      
      // Auto-calculate certificate-level growth origin
      const certificateGrowthOrigin = autoSelectCertificateGrowthOrigin(newSelections);
      
      return {
        ...prev,
        gemstone_selections: newSelections,
        gemstone_growth_origin: certificateGrowthOrigin
      };
    });
  };

  // Handle diamond field updates (for color/clarity)
  const handleDiamondFieldUpdate = (gemstoneType: string, field: string, value: any) => {
    if (fieldsLocked) {
      console.log('🔒 Diamond field update blocked - form is locked');
      return;
    }
    setFormData(prev => ({
      ...prev,
      gemstone_selections: prev.gemstone_selections.map(s => {
        if (s.gemstone_type === gemstoneType) {
          const updatedSelection = { ...s, [field]: value };
          
          // Handle unified diamond type (applies to both color and clarity)
          if (field === 'diamond_type') {
            if (value === 'FIXED') {
              // Set both color and clarity to FIXED, clear range values
              updatedSelection.diamond_color_type = 'FIXED';
              updatedSelection.diamond_clarity_type = 'FIXED';
              updatedSelection.diamond_color_range = undefined;
              updatedSelection.diamond_clarity_range = undefined;
              updatedSelection.diamond_color_fixed = updatedSelection.diamond_color_fixed || '';
              updatedSelection.diamond_clarity_fixed = updatedSelection.diamond_clarity_fixed || '';
            } else if (value === 'RANGE') {
              // Set both color and clarity to RANGE, clear fixed values
              updatedSelection.diamond_color_type = 'RANGE';
              updatedSelection.diamond_clarity_type = 'RANGE';
              updatedSelection.diamond_color_fixed = undefined;
              updatedSelection.diamond_clarity_fixed = undefined;
              updatedSelection.diamond_color_range = updatedSelection.diamond_color_range || 'G-H';
              updatedSelection.diamond_clarity_range = updatedSelection.diamond_clarity_range || 'VS-SI';
            }
          }
          
          // Legacy handling for individual color/clarity type changes (keep for backward compatibility)
          if (field === 'diamond_color_type') {
            if (value === 'FIXED') {
              updatedSelection.diamond_color_range = undefined;
              updatedSelection.diamond_color_fixed = updatedSelection.diamond_color_fixed || '';
            } else if (value === 'RANGE') {
              updatedSelection.diamond_color_fixed = undefined;
              updatedSelection.diamond_color_range = updatedSelection.diamond_color_range || 'G-H';
            }
          }
          
          if (field === 'diamond_clarity_type') {
            if (value === 'FIXED') {
              updatedSelection.diamond_clarity_range = undefined;
              updatedSelection.diamond_clarity_fixed = updatedSelection.diamond_clarity_fixed || '';
            } else if (value === 'RANGE') {
              updatedSelection.diamond_clarity_fixed = undefined;
              updatedSelection.diamond_clarity_range = updatedSelection.diamond_clarity_range || 'VS-SI';
            }
          }
          
          return updatedSelection;
        }
        return s;
      })
    }))
  }

  // Handle pearl diameter blur (format only, no auto-correction)
  const handlePearlDiameterBlur = (gemstoneType: string, diameter: string) => {
    console.log(`🔍 Pearl diameter blur ${gemstoneType}: "${diameter}"`);
    
    // Only format if there's a valid value, don't auto-correct
    if (diameter && diameter.trim() !== '') {
      const num = parseFloat(diameter);
      if (!isNaN(num) && num > 0) {
        const finalDiameter = num.toFixed(2); // Always format to 2 decimal places
        setFormData(prev => ({
          ...prev,
          gemstone_selections: prev.gemstone_selections.map(s =>
            s.gemstone_type === gemstoneType ? { ...s, measurement_value: finalDiameter } : s
          )
        }));
      }
    }
    // If empty or invalid, leave as-is and let validation handle it
  };

  // Handle pearl range blur (format only, no auto-correction)
  const handlePearlRangeBlur = (gemstoneType: string, isMin: boolean, value: string) => {
    console.log(`🔍 Pearl range blur ${gemstoneType} ${isMin ? 'min' : 'max'}: "${value}"`);
    
    // Only format if there's a valid value, don't auto-correct
    if (value && value.trim() !== '') {
      const num = parseFloat(value);
      if (!isNaN(num) && num > 0) {
        const finalValue = num.toFixed(2); // Always format to 2 decimal places
        
        if (isMin) {
          handlePearlRangeMinChange(gemstoneType, finalValue);
        } else {
          handlePearlRangeMaxChange(gemstoneType, finalValue);
        }
      }
    }
    // If empty or invalid, leave as-is and let validation handle it
  };

  // Enhanced form submission validation with strict checking
  const canSubmitForm = () => {
    console.log('🔍 Checking if form can be submitted...');
    
    // Check if we have any gemstones selected
    if (formData.gemstone_selections.length === 0) {
      console.log('❌ No gemstones selected');
      return false;
    }
    
    // Check all gemstone measurements for critical errors
    for (const selection of formData.gemstone_selections) {
      const config = getGemstoneConfig(selection.gemstone_type);
      
      if (config?.default_measurement_type === 'DIAMETER') {
        // Pearl validation
        if (config.supports_ranges) {
          // Range validation
          const validation = getValidationMessage(selection.measurement_range || '', selection.gemstone_type);
          console.log(`🔍 Pearl range validation for ${selection.gemstone_type}:`, validation);
          if (!validation.allowSubmit) {
            console.log(`❌ Critical error in ${selection.gemstone_type}: ${validation.message}`);
            return false;
          }
        } else {
          // Single diameter validation
          const validation = getValidationMessage(selection.measurement_value || '', selection.gemstone_type);
          console.log(`🔍 Pearl diameter validation for ${selection.gemstone_type}:`, validation);
          if (!validation.allowSubmit) {
            console.log(`❌ Critical error in ${selection.gemstone_type}: ${validation.message}`);
            return false;
          }
        }
      } else {
        // Traditional gemstone weight validation
        const weightValue = selection.carat_weight || selection.measurement_value || '';
        const validation = getValidationMessage(weightValue, selection.gemstone_type);
        console.log(`🔍 Weight validation for ${selection.gemstone_type}:`, validation);
        if (!validation.allowSubmit) {
          console.log(`❌ Critical error in ${selection.gemstone_type}: ${validation.message}`);
          return false;
        }
      }
    }
    
    // Check required fields
    if (!formData.cert_id || !formData.metal_composition || !formData.item_gross_weight) {
      console.log('❌ Missing required fields');
      return false;
    }
    
    // Check item weight
    const itemWeight = parseFloat(formData.item_gross_weight);
    if (isNaN(itemWeight) || itemWeight <= 0) {
      console.log('❌ Invalid item weight');
      return false;
    }
    
    console.log('✅ Form can be submitted');
    return true;
  };

  return (
    <div className="certificate-creation-container">
      {/* Top Right Navigation Buttons */}
      <div className="top-navigation">
        <button
          type="button"
          onClick={handleReturnToDashboard}
          className="dashboard-btn top-dashboard"
          title="Return to Dashboard"
        >
          🏠 Dashboard
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="logout-btn top-logout"
          title="Logout"
        >
          🚪 Logout
        </button>
      </div>
      
      <div className="split-screen-layout">
        {/* Left Side - Creation Form */}
        <div className="creation-form-side">
          <div className="certificate-creation-panel">
            {/* EGL Logo */}
            <div className="logo-section">
              <img 
                src="https://verifygold.s3.me-central-1.amazonaws.com/EGL/Logo/EGL_Logo.svg" 
                alt="European Gemological Lab" 
                className="egl-logo"
              />
              <h1>Certificate Creation</h1>
            </div>

        {/* Session Warning */}
        {sessionWarning && (
          <div className="session-warning">
            ⏰ {sessionWarning}
          </div>
        )}

        {/* Status Messages */}
        {statusMessages.length > 0 && (
          <div className="status-messages">
            {statusMessages.map((msg, index) => (
              <div key={index} className={`status-message ${msg.type}`}>
                {msg.message}
              </div>
            ))}
          </div>
        )}

        {/* Certificate Design Template */}
        <div className="form-section">
          <h3>Certificate Design Template</h3>
          <div className="design-template-grid">
            <div className="form-field">
              <div className="single-select">
                <label>
                  <input
                    type="radio"
                    name="card_design"
                    value="EUROPEANGEMOLOGICALLAB"
                    checked={formData.card_design_code === 'EUROPEANGEMOLOGICALLAB'}
                    onChange={(e) => handleSingleSelection('card_design_code', e.target.value)}
                    onClick={(e) => {
                      console.log('🔍 Card Design Radio onClick:', 'EUROPEANGEMOLOGICALLAB')
                      e.stopPropagation()
                      handleSingleSelection('card_design_code', 'EUROPEANGEMOLOGICALLAB')
                    }}
                    disabled={fieldsLocked}
                  />
                  <span>European Gemological Laboratory</span>
                </label>
              </div>
            </div>
            <div className="form-field">
              {/* Empty column for future design templates */}
              <div className="placeholder-text" style={{ color: '#9ca3af', fontStyle: 'italic', padding: '0.5rem' }}>
                Additional design templates coming soon...
              </div>
            </div>
          </div>
        </div>

        {/* Certificate Properties */}
        <div className="form-section">
          <h3>Certificate Properties</h3>
          
          <div className="certificate-properties-grid">
            <div className="form-field">
            <label>Certificate URL UID (Auto-populated)</label>
            <input
              type="text"
              value={certificateUuid || ''}
              disabled
              className="disabled-field"
              placeholder="Will be generated after certificate creation"
            />
          </div>

          <div className="form-field">
            <label>User ID (Auto-populated)</label>
            <input
              type="text"
              value={user?.id || 'current-user-id'} // TODO: Get from auth context
              disabled
              className="disabled-field"
            />
          </div>

          <div className="form-field">
            <label>Certificate ID *</label>
            <input
              type="text"
              value={formData.cert_id}
              onChange={(e) => {
                // Filter to only allow A-Z and 0-9, then convert to uppercase
                const filteredValue = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
                setFormData(prev => ({ ...prev, cert_id: filteredValue }))
                validateCertIdField('cert_id', filteredValue)
              }}
              onKeyDown={(e) => {
                // Allow control keys (backspace, delete, arrow keys, etc.)
                if (e.ctrlKey || e.metaKey || e.altKey || 
                    ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(e.key)) {
                  return
                }
                // Only allow A-Z and 0-9
                if (!/^[A-Za-z0-9]$/.test(e.key)) {
                  e.preventDefault()
                }
              }}
              disabled={fieldsLocked}
              maxLength={15}
              className={
                validationState.cert_id?.isChecking ? 'checking' :
                validationState.cert_id?.isValid === true ? 'valid' :
                validationState.cert_id?.isValid === false ? 'invalid' : ''
              }
              placeholder="Enter certificate ID (A-Z, 0-9 only)"
            />
            <span className="char-count">
              {formData.cert_id.length}/15
            </span>
            {validationState.cert_id && (
              <div className={`validation-message ${validationState.cert_id.isValid ? 'valid' : 'invalid'}`}>
                {validationState.cert_id.isChecking ? 'Checking availability...' : validationState.cert_id.message}
              </div>
            )}
          </div>

          <div className="form-field">
            <label>Confirm Certificate ID *</label>
            <input
              type="text"
              value={formData.cert_id_confirm || ''}
              onChange={(e) => {
                // Filter to only allow A-Z and 0-9, then convert to uppercase
                const filteredValue = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
                setFormData(prev => ({ ...prev, cert_id_confirm: filteredValue }))
              }}
              onKeyDown={(e) => {
                // Allow control keys (backspace, delete, arrow keys, etc.)
                if (e.ctrlKey || e.metaKey || e.altKey || 
                    ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(e.key)) {
                  return
                }
                // Only allow A-Z and 0-9
                if (!/^[A-Za-z0-9]$/.test(e.key)) {
                  e.preventDefault()
                }
              }}
              onPaste={(e) => {
                // Prevent paste operations
                e.preventDefault()
                addStatusMessage('Please type the Certificate ID confirmation manually - copy/paste is not allowed', 'warning')
              }}
              onDrop={(e) => {
                // Prevent drag and drop
                e.preventDefault()
                addStatusMessage('Please type the Certificate ID confirmation manually - drag/drop is not allowed', 'warning')
              }}
              disabled={fieldsLocked}
              maxLength={15}
              className={
                formData.cert_id_confirm && formData.cert_id && formData.cert_id_confirm === formData.cert_id && 
                validationState.cert_id?.isValid === true ? 'valid' :
                formData.cert_id_confirm && formData.cert_id && 
                (formData.cert_id_confirm !== formData.cert_id || validationState.cert_id?.isValid === false) ? 'invalid' : ''
              }
              placeholder="Re-type certificate ID to confirm"
            />
            <span className="char-count">
              {(formData.cert_id_confirm || '').length}/15
            </span>
            {formData.cert_id_confirm && formData.cert_id && (
              <div className={`validation-message ${
                formData.cert_id_confirm === formData.cert_id && 
                validationState.cert_id?.isValid === true ? 'valid' : 'invalid'
              }`}>
                {formData.cert_id_confirm === formData.cert_id ? 
                  (validationState.cert_id?.isValid === true ? 
                    '✓ Certificate IDs match' : 
                    'Certificate ID matches but Certificate ID already exists. Please provide a unique Certificate ID') : 
                  '✗ Certificate IDs do not match'}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Gemstone Properties */}
        <div className="form-section">
          <h3>Gemstone Properties</h3>

          <div className="form-field">
            <label>Gemstone Type(s) * (Select 1-3)</label>
            <div className="checkbox-grid">
              {referenceData.gemstone_types.map(gemstone => (
                <label 
                  key={gemstone.gemstone_type} 
                  className={`checkbox-item ${formData.gemstone_selections.some(s => s.gemstone_type === gemstone.gemstone_type) ? 'checked' : ''} ${
                    (fieldsLocked && !formData.gemstone_selections.some(s => s.gemstone_type === gemstone.gemstone_type) && !canSelectDiamond(gemstone.gemstone_type)) ? 'sequential-disabled' : ''
                  } ${
                    (!fieldsLocked && !formData.gemstone_selections.some(s => s.gemstone_type === gemstone.gemstone_type) && !canSelectDiamond(gemstone.gemstone_type)) ? 'sequential-restricted' : ''
                  }`}
                  onClick={(e) => {
                    console.log('🔍 Label clicked:', gemstone.gemstone_type)
                    // Don't prevent default - let the checkbox handle it
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.gemstone_selections.some(s => s.gemstone_type === gemstone.gemstone_type)}
                    onChange={(e) => {
                      console.log('🔍 Input onChange:', gemstone.gemstone_type, e.target.checked)
                      handleGemstoneTypeChange(gemstone.gemstone_type, e.target.checked)
                    }}
                    onClick={(e) => {
                      console.log('🔍 Input onClick:', gemstone.gemstone_type)
                      // Force the change if onChange isn't working
                      const newChecked = !formData.gemstone_selections.some(s => s.gemstone_type === gemstone.gemstone_type)
                      console.log('🔍 Forcing change:', gemstone.gemstone_type, newChecked)
                      e.preventDefault()
                      e.stopPropagation()
                      handleGemstoneTypeChange(gemstone.gemstone_type, newChecked)
                    }}
                    disabled={
                      fieldsLocked || 
                      (formData.gemstone_selections.length >= 3 && !formData.gemstone_selections.some(s => s.gemstone_type === gemstone.gemstone_type)) ||
                      (!formData.gemstone_selections.some(s => s.gemstone_type === gemstone.gemstone_type) && !canSelectDiamond(gemstone.gemstone_type))
                    }
                  />
                  <span>{gemstone.gemstone_type} {gemstone.abbreviation && `(${gemstone.abbreviation})`}</span>
                </label>
              ))}
            </div>
            {/* No validation message for gemstone_types here, as it's handled by the new state update */}
            {formData.gemstone_selections.length > 0 && (
              <div className="selected-types">
                Selected: {displaySelectedGemstones()}
              </div>
            )}
          </div>

          {/* Individual Gemstone Weights */}
          {formData.gemstone_selections.length > 0 && (
            <div className="form-group">
              <label>Individual Gemstone Measurements *</label>
              <div className="gemstone-weights">
                {formData.gemstone_selections.map((selection, index) => {
                  const gemstone = referenceData.gemstone_types.find(g => g.gemstone_type === selection.gemstone_type);
                  const config = getGemstoneConfig(selection.gemstone_type);
                  const validation = getWeightValidation(selection.gemstone_type);
                  
                  return (
                    <div key={selection.gemstone_type} className="gemstone-weight-input">
                      <label>{gemstone?.gemstone_type} ({gemstone?.abbreviation})</label>
                      
                      {config?.default_measurement_type === 'DIAMETER' ? (
                        // Pearl diameter inputs
                        <div className="pearl-measurement">
                          {/* Pearl measurement type selector */}
                          <div className="pearl-type-selector">
                            <label 
                              className={`radio-option ${!selection.measurement_range || selection.measurement_range === '' ? 'checked' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (!fieldsLocked) {
                                  setFormData(prev => ({
                                    ...prev,
                                    gemstone_selections: prev.gemstone_selections.map(s =>
                                      s.gemstone_type === selection.gemstone_type
                                        ? { ...s, measurement_range: undefined, measurement_value: s.measurement_value || '' }
                                        : s
                                    )
                                  }));
                                }
                              }}
                            >
                              <input
                                type="radio"
                                name={`pearl_type_${selection.gemstone_type}`}
                                checked={!selection.measurement_range || selection.measurement_range === ''}
                                onChange={() => {}}
                                disabled={fieldsLocked}
                              />
                              <span className="radio-label">Single Diameter</span>
                            </label>
                            <label 
                              className={`radio-option ${!!selection.measurement_range && selection.measurement_range !== '' ? 'checked' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (!fieldsLocked) {
                                  setFormData(prev => ({
                                    ...prev,
                                    gemstone_selections: prev.gemstone_selections.map(s =>
                                      s.gemstone_type === selection.gemstone_type
                                        ? { ...s, measurement_value: undefined, measurement_range: '-' }
                                        : s
                                    )
                                  }));
                                }
                              }}
                            >
                              <input
                                type="radio"
                                name={`pearl_type_${selection.gemstone_type}`}
                                checked={!!selection.measurement_range && selection.measurement_range !== ''}
                                onChange={() => {}}
                                disabled={fieldsLocked}
                              />
                              <span className="radio-label">Diameter Range</span>
                            </label>
                          </div>
                          
                          {selection.measurement_range && selection.measurement_range !== '' ? (
                            <div className="pearl-range-inputs">
                              <div className="range-input-pair">
                                <div className="input-with-suffix">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="30"
                                    inputMode="decimal"
                                    pattern="[0-9]+(\.[0-9]{1,2})?"
                                    value={selection.measurement_range?.split('-')[0] || ''}
                                    onChange={(e) => handlePearlRangeMinChange(selection.gemstone_type, e.target.value)}
                                    onBlur={(e) => handlePearlRangeBlur(selection.gemstone_type, true, e.target.value)}
                                    onKeyPress={(e) => {
                                      // Only allow numbers, decimal point, and control keys
                                      if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={fieldsLocked}
                                    className={`diameter-input ${validation?.type || ''}`}
                                    placeholder="Min"
                                  />
                                  <span className="suffix">mm</span>
                                </div>
                                <span className="range-separator">-</span>
                                <div className="input-with-suffix">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="30"
                                    inputMode="decimal"
                                    pattern="[0-9]+(\.[0-9]{1,2})?"
                                    value={selection.measurement_range?.split('-')[1] || ''}
                                    onChange={(e) => handlePearlRangeMaxChange(selection.gemstone_type, e.target.value)}
                                    onBlur={(e) => handlePearlRangeBlur(selection.gemstone_type, false, e.target.value)}
                                    onKeyPress={(e) => {
                                      // Only allow numbers, decimal point, and control keys
                                      if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={fieldsLocked}
                                    className={`diameter-input ${validation?.type || ''}`}
                                    placeholder="Max"
                                  />
                                  <span className="suffix">mm</span>
                                </div>
                              </div>
                              <div className="range-result">
                                {selection.measurement_range && selection.measurement_range !== '-' 
                                  ? `Result: ${selection.measurement_range} mm`
                                  : 'Result: Enter range above'
                                }
                              </div>
                            </div>
                          ) : (
                            <div className="pearl-single-input">
                              <div className="input-with-suffix">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="30"
                                  inputMode="decimal"
                                  pattern="[0-9]+(\.[0-9]{1,2})?"
                                  value={selection.measurement_value || ''}
                                  onChange={(e) => handlePearlDiameterChange(selection.gemstone_type, e.target.value)}
                                  onBlur={(e) => handlePearlDiameterBlur(selection.gemstone_type, e.target.value)}
                                  onKeyPress={(e) => {
                                    // Only allow numbers, decimal point, and control keys
                                    if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  disabled={fieldsLocked}
                                  className={`diameter-input ${validation?.type || ''}`}
                                  placeholder="Diameter"
                                />
                                <span className="suffix">mm</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Traditional gemstone weight input
                        <div className="input-with-suffix">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            pattern="[0-9]+(\.[0-9]+)?"
                            value={selection.carat_weight || selection.measurement_value || ''}
                            onChange={(e) => handleGemstoneWeightChange(selection.gemstone_type, e.target.value)}
                            onBlur={(e) => handleGemstoneWeightBlur(selection.gemstone_type, e.target.value)}
                            onKeyPress={(e) => {
                              // Only allow numbers, decimal point, and control keys
                              if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={fieldsLocked}
                            className={`weight-input ${validation?.type || ''}`}
                            placeholder="Weight"
                          />
                          <span className="suffix">Carats</span>
                        </div>
                      )}
                      
                      {/* Validation message positioned right after weight input */}
                      {validation && (
                        <div className={`validation-message ${validation.type}`}>
                          {validation.message}
                        </div>
                      )}
                      
                      {/* Individual Growth Origin Selection (not for pearls) */}
                      {!selection.gemstone_type.includes('PEARL') && (
                        <div className="growth-origin-selection">
                          <label>Growth Origin *</label>
                          <div className="radio-group">
                            <label 
                              className={`radio-option ${selection.growth_origin === 'NATURAL' ? 'checked' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                console.log('🔍 Natural label clicked for:', selection.gemstone_type);
                                handleIndividualGrowthOriginChange(selection.gemstone_type, 'NATURAL');
                              }}
                            >
                              <input
                                type="radio"
                                name={`growth_origin_${selection.gemstone_type}`}
                                value="NATURAL"
                                checked={selection.growth_origin === 'NATURAL'}
                                onChange={(e) => {
                                  console.log('🔍 Natural radio onChange for:', selection.gemstone_type);
                                  handleIndividualGrowthOriginChange(selection.gemstone_type, 'NATURAL');
                                }}
                                onClick={(e) => {
                                  console.log('🔍 Natural radio onClick for:', selection.gemstone_type);
                                  e.stopPropagation();
                                  handleIndividualGrowthOriginChange(selection.gemstone_type, 'NATURAL');
                                }}
                                disabled={fieldsLocked}
                              />
                              <span>Natural</span>
                            </label>
                            <label 
                              className={`radio-option ${selection.growth_origin === 'LAB GROWN' ? 'checked' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                console.log('🔍 Lab Grown label clicked for:', selection.gemstone_type);
                                handleIndividualGrowthOriginChange(selection.gemstone_type, 'LAB GROWN');
                              }}
                            >
                              <input
                                type="radio"
                                name={`growth_origin_${selection.gemstone_type}`}
                                value="LAB GROWN"
                                checked={selection.growth_origin === 'LAB GROWN'}
                                onChange={(e) => {
                                  console.log('🔍 Lab Grown radio onChange for:', selection.gemstone_type);
                                  handleIndividualGrowthOriginChange(selection.gemstone_type, 'LAB GROWN');
                                }}
                                onClick={(e) => {
                                  console.log('🔍 Lab Grown radio onClick for:', selection.gemstone_type);
                                  e.stopPropagation();
                                  handleIndividualGrowthOriginChange(selection.gemstone_type, 'LAB GROWN');
                                }}
                                disabled={fieldsLocked}
                              />
                              <span>Lab Grown</span>
                            </label>
                          </div>
                        </div>
                      )}
                      
                      {/* Diamond Color & Clarity Selection (only for DIAMOND_X gemstone_type) */}
                      {selection.gemstone_type.startsWith('DIAMOND_') && (
                        <div className="diamond-properties">
                          {/* Single Diamond Type Selection - Applies to BOTH Color and Clarity */}
                          <div className="diamond-type-header">
                            <label>Diamond Selection Type *</label>
                            <div className="diamond-type-selection">
                              <label className={`radio-option ${selection.diamond_color_type === 'FIXED' ? 'checked' : ''}`}
                                onClick={(e) => {
                                  console.log('🔍 Diamond Fixed (both color & clarity) label clicked for:', selection.gemstone_type);
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_type', 'FIXED');
                                }}>
                                <input
                                  type="radio"
                                  name={`diamond_type_${selection.gemstone_type}`}
                                  value="FIXED"
                                  checked={selection.diamond_color_type === 'FIXED'}
                                  onChange={(e) => {
                                    console.log('🔍 Diamond Fixed radio onChange for:', selection.gemstone_type);
                                    handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_type', 'FIXED');
                                  }}
                                  onClick={(e) => {
                                    console.log('🔍 Diamond Fixed radio onClick for:', selection.gemstone_type);
                                    e.stopPropagation();
                                    handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_type', 'FIXED');
                                  }}
                                  disabled={fieldsLocked}
                                />
                                <span>Fixed (Specific Color & Clarity)</span>
                              </label>
                              <label className={`radio-option ${selection.diamond_color_type === 'RANGE' ? 'checked' : ''}`}
                                onClick={(e) => {
                                  console.log('🔍 Diamond Range (both color & clarity) label clicked for:', selection.gemstone_type);
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_type', 'RANGE');
                                }}>
                                <input
                                  type="radio"
                                  name={`diamond_type_${selection.gemstone_type}`}
                                  value="RANGE"
                                  checked={selection.diamond_color_type === 'RANGE'}
                                  onChange={(e) => {
                                    console.log('🔍 Diamond Range radio onChange for:', selection.gemstone_type);
                                    handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_type', 'RANGE');
                                  }}
                                  onClick={(e) => {
                                    console.log('🔍 Diamond Range radio onClick for:', selection.gemstone_type);
                                    e.stopPropagation();
                                    handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_type', 'RANGE');
                                  }}
                                  disabled={fieldsLocked}
                                />
                                <span>Range (Color & Clarity Ranges)</span>
                              </label>
                            </div>
                          </div>
                          
                          {/* Diamond Color Selection */}
                          <div className="diamond-color-section">
                            <label>Diamond Color *</label>
                            
                            {/* Fixed Color Options - Checkboxes */}
                            {selection.diamond_color_type === 'FIXED' && (
                              <div className="diamond-options-grid">
                                {diamondReferenceData.diamond_colors.map(color => (
                                  <label key={color.color_code} className={`checkbox-option ${selection.diamond_color_fixed === color.color_code ? 'checked' : ''}`}
                                    onClick={(e) => {
                                      console.log('🔍 Diamond Color Fixed checkbox label clicked:', color.color_code, 'for:', selection.gemstone_type);
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_color_fixed', 
                                        selection.diamond_color_fixed === color.color_code ? '' : color.color_code);
                                    }}>
                                    <input
                                      type="checkbox"
                                      checked={selection.diamond_color_fixed === color.color_code}
                                      onChange={(e) => {
                                        console.log('🔍 Diamond Color Fixed checkbox onChange:', color.color_code, 'for:', selection.gemstone_type);
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_color_fixed', 
                                          selection.diamond_color_fixed === color.color_code ? '' : color.color_code);
                                      }}
                                      onClick={(e) => {
                                        console.log('🔍 Diamond Color Fixed checkbox onClick:', color.color_code, 'for:', selection.gemstone_type);
                                        e.stopPropagation();
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_color_fixed', 
                                          selection.diamond_color_fixed === color.color_code ? '' : color.color_code);
                                      }}
                                      disabled={fieldsLocked}
                                    />
                                    <span className="checkbox-label">
                                      <strong>{color.color_code}</strong>
                                      <small>{color.description}</small>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                            
                            {/* Color Range Options - Checkboxes */}
                            {selection.diamond_color_type === 'RANGE' && (
                              <div className="diamond-options-grid">
                                {diamondReferenceData.diamond_color_ranges.map(range => (
                                  <label key={range.range_code_code} className={`checkbox-option ${selection.diamond_color_range === range.range_code_code ? 'checked' : ''}`}
                                    onClick={(e) => {
                                      console.log('🔍 Diamond Color Range checkbox label clicked:', range.range_code_code, 'for:', selection.gemstone_type);
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_color_range', 
                                        selection.diamond_color_range === range.range_code ? '' : range.range_code);
                                    }}>
                                    <input
                                      type="checkbox"
                                      checked={selection.diamond_color_range === range.range_code}
                                      onChange={(e) => {
                                        console.log('🔍 Diamond Color Range checkbox onChange:', range.range_code, 'for:', selection.gemstone_type);
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_color_range', 
                                          selection.diamond_color_range === range.range_code ? '' : range.range_code);
                                      }}
                                      onClick={(e) => {
                                        console.log('🔍 Diamond Color Range checkbox onClick:', range.range_code, 'for:', selection.gemstone_type);
                                        e.stopPropagation();
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_color_range', 
                                          selection.diamond_color_range === range.range_code ? '' : range.range_code);
                                      }}
                                      disabled={fieldsLocked}
                                    />
                                    <span className="checkbox-label">
                                      <strong>{range.range_code}</strong>
                                      <small>{range.description}</small>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Diamond Clarity Selection */}
                          <div className="diamond-clarity-section">
                            <label>Diamond Clarity *</label>
                            
                            {/* Fixed Clarity Options - Checkboxes */}
                            {selection.diamond_color_type === 'FIXED' && (
                              <div className="diamond-options-grid">
                                {diamondReferenceData.diamond_clarity.map(clarity => (
                                  <label key={clarity.clarity_code} className={`checkbox-option ${selection.diamond_clarity_fixed === clarity.clarity_code ? 'checked' : ''}`}
                                    onClick={(e) => {
                                      console.log('🔍 Diamond Clarity Fixed checkbox label clicked:', clarity.clarity_code, 'for:', selection.gemstone_type);
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_clarity_fixed', 
                                        selection.diamond_clarity_fixed === clarity.clarity_code ? '' : clarity.clarity_code);
                                    }}>
                                    <input
                                      type="checkbox"
                                      checked={selection.diamond_clarity_fixed === clarity.clarity_code}
                                      onChange={(e) => {
                                        console.log('🔍 Diamond Clarity Fixed checkbox onChange:', clarity.clarity_code, 'for:', selection.gemstone_type);
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_clarity_fixed', 
                                          selection.diamond_clarity_fixed === clarity.clarity_code ? '' : clarity.clarity_code);
                                      }}
                                      onClick={(e) => {
                                        console.log('🔍 Diamond Clarity Fixed checkbox onClick:', clarity.clarity_code, 'for:', selection.gemstone_type);
                                        e.stopPropagation();
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_clarity_fixed', 
                                          selection.diamond_clarity_fixed === clarity.clarity_code ? '' : clarity.clarity_code);
                                      }}
                                      disabled={fieldsLocked}
                                    />
                                    <span className="checkbox-label">
                                      <strong>{clarity.clarity_code}</strong>
                                      <small>{clarity.description}</small>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                            
                            {/* Clarity Range Options - Checkboxes */}
                            {selection.diamond_color_type === 'RANGE' && (
                              <div className="diamond-options-grid">
                                {diamondReferenceData.diamond_clarity_ranges.map(range => (
                                  <label key={range.range_code_code} className={`checkbox-option ${selection.diamond_clarity_range === range.range_code_code ? 'checked' : ''}`}
                                    onClick={(e) => {
                                      console.log('🔍 Diamond Clarity Range checkbox label clicked:', range.range_code_code, 'for:', selection.gemstone_type);
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_clarity_range', 
                                        selection.diamond_clarity_range === range.range_code ? '' : range.range_code);
                                    }}>
                                    <input
                                      type="checkbox"
                                      checked={selection.diamond_clarity_range === range.range_code}
                                      onChange={(e) => {
                                        console.log('🔍 Diamond Clarity Range checkbox onChange:', range.range_code, 'for:', selection.gemstone_type);
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_clarity_range', 
                                          selection.diamond_clarity_range === range.range_code ? '' : range.range_code);
                                      }}
                                      onClick={(e) => {
                                        console.log('🔍 Diamond Clarity Range checkbox onClick:', range.range_code, 'for:', selection.gemstone_type);
                                        e.stopPropagation();
                                        handleDiamondFieldUpdate(selection.gemstone_type, 'diamond_clarity_range', 
                                          selection.diamond_clarity_range === range.range_code ? '' : range.range_code);
                                      }}
                                      disabled={fieldsLocked}
                                    />
                                    <span className="checkbox-label">
                                      <strong>{range.range_code}</strong>
                                      <small>{range.description}</small>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              

            </div>
          )}



          <div className="form-field">
            <label>Gemstone Growth Origin * (Auto-calculated)</label>
            <div className="radio-group auto-calculated">
              {referenceData.growth_origins.map(origin => (
                <label 
                  key={origin.growth_origin}
                  className={`radio-option ${formData.gemstone_growth_origin === origin.growth_origin ? 'checked auto-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="auto_growth_origin"
                    value={origin.growth_origin}
                    checked={formData.gemstone_growth_origin === origin.growth_origin}
                    disabled={true}
                    readOnly={true}
                  />
                  <span>{origin.growth_origin} {origin.abbreviation && `(${origin.abbreviation})`}</span>
                </label>
              ))}
            </div>
            <div className="calculation-note">
              Automatically determined based on individual gemstone selections above
            </div>
          </div>

          {/* Gemstone Gross Weight Field */}
          {formData.gemstone_selections.some(s => s.gemstone_type && !s.gemstone_type.includes('PEARL')) && (
            <div className="form-field">
              <label>Gemstone Gross Weight (Auto-calculated)</label>
              <div className="input-with-suffix">
                <input
                  type="text"
                  value={calculateGemstoneTotalWeight()}
                  disabled
                  className="disabled-field auto-calculated-weight"
                  placeholder="0.00"
                />
                <span className="suffix">Carats</span>
              </div>
            </div>
          )}
        </div>

        {/* Metal Properties */}
        <div className="form-section">
          <h3>Metal Properties</h3>

          <div className="metal-properties-grid">
            <div className="form-field">
            <label>Metal Composition *</label>
            <div className="radio-group">
              {referenceData.metal_compositions.map(metal => (
                <label 
                  key={metal.metal_composition}
                  className={formData.metal_composition === metal.metal_composition ? 'checked' : ''}
                >
                  <input
                    type="radio"
                    name="metal_composition"
                    value={metal.metal_composition}
                    checked={formData.metal_composition === metal.metal_composition}
                    onChange={(e) => handleSingleSelection('metal_composition', e.target.value)}
                    onClick={(e) => {
                      console.log('🔍 Metal Composition Radio onClick:', metal.metal_composition)
                      e.stopPropagation()
                      handleSingleSelection('metal_composition', metal.metal_composition)
                    }}
                    disabled={fieldsLocked}
                  />
                  <span>{metal.metal_composition} {metal.abbreviation && `(${metal.abbreviation})`}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-field">
            <label>Item Gross Weight * (GRAMS)</label>
            <div className="input-with-suffix">
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                pattern="[0-9]+(\.[0-9]+)?"
                value={formData.item_gross_weight}
                onChange={(e) => handleWeightInput('item_gross_weight', e.target.value)}
                onBlur={(e) => handleWeightBlur('item_gross_weight', e.target.value)}
                onKeyPress={(e) => {
                  // Only allow numbers, decimal point, and control keys
                  if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                disabled={fieldsLocked}
                className={
                  validationState.item_gross_weight?.isValid === true ? 'valid' :
                  validationState.item_gross_weight?.isValid === false ? 'invalid' : ''
                }
                placeholder="Weight"
              />
              <span className="suffix">GRAMS</span>
            </div>
            {validationState.item_gross_weight && (
              <div className={`validation-message ${validationState.item_gross_weight.isValid ? 'valid' : 'invalid'}`}>
                {validationState.item_gross_weight.message}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Selection Summary */}
        {(formData.cert_id || formData.gemstone_selections.some(s => s.gemstone_type) || formData.metal_composition || formData.item_gross_weight) && (
          <div className="form-section">
            <h3>Selection Summary</h3>
            <div className="gemstone-summary-container">
              
              {/* Certificate ID Summary */}
              <div className="gemstone-summary-item">
                <h4>Certificate Information</h4>
                <div className="summary-details">
                  <div className="summary-detail">
                    Certificate ID: <span className={
                      !formData.cert_id ? 'missing-field' : 
                      (validationState.cert_id && !validationState.cert_id.isValid) ? 'duplicate-field' : ''
                    }>
                      {!formData.cert_id 
                        ? 'Not entered' 
                        : (validationState.cert_id && !validationState.cert_id.isValid)
                          ? `${formData.cert_id} - Duplicate, needs to be changed`
                          : formData.cert_id
                      }
                    </span>
                  </div>
                </div>
              </div>



              {/* Metal Properties Summary */}
              {(formData.metal_composition || formData.item_gross_weight) && (
                <div className="gemstone-summary-item">
                  <h4>Metal Properties</h4>
                  <div className="summary-details">
                    <div className="summary-detail">
                      Metal Composition: <span className={!formData.metal_composition ? 'missing-field' : ''}>{formData.metal_composition || 'Not selected'}</span>
                    </div>
                    <div className="summary-detail">
                      Item Gross Weight: <span className={!formData.item_gross_weight ? 'missing-field' : ''}>{formData.item_gross_weight ? `${formData.item_gross_weight} grams` : 'Not entered'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Gemstone Summary */}
              {generateGemstoneSummary().map((item, index, array) => {
                const isDiamond = item.type === 'diamond';
                const diamonds = array.filter(i => i.type === 'diamond');
                const isLastDiamond = isDiamond && diamonds.length > 1 && index === diamonds.length - 1;
                
                return (
                  <div key={index}>
                    <div className="gemstone-summary-item">
                      <h4>{item.name}</h4>
                      <div className="summary-details">
                        {item.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className={`summary-detail ${detail.missing ? 'missing-field' : ''}`}>
                            {detail.text}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Diamond Running Total (appears after last diamond if multiple diamonds) */}
                    {isLastDiamond && (
                      <div className="diamond-total-summary">
                        <strong>Diamond Carats Running Total: {calculateDiamondTotal()}</strong>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Gemstone Gross Weight Total (appears at the end if any gemstones with carat weight exist) */}
              {formData.gemstone_selections.some(s => s.gemstone_type && !s.gemstone_type.includes('PEARL') && s.carat_weight) && (
                <div className="gemstone-total-summary">
                  <strong>Gemstone Gross Weight (Auto-calculated): {calculateGemstoneTotalWeight()} carats</strong>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Optional Fields */}
        <div className="form-section">
          <h3>Optional Fields</h3>

          <div className="comment-lines">
            <h4>Comment Lines (65 characters max, ASCII only)</h4>
            <div className="comment-lines-grid">
              {[1, 2, 3].map(num => {
              const fieldKey = `comment_line_${num}` as keyof typeof fieldStates;
              const fieldState = fieldStates[fieldKey];
              const isAutoPopulated = fieldState?.isAutoPopulated || false;
              const isEditable = fieldState?.isEditable !== false;
              
              return (
                <div key={num} className={`form-field ${isAutoPopulated ? 'has-auto-populated' : 'has-user-editable'}`}>
                  <label>Comment Line {num}</label>
                  <input
                    type="text"
                    value={formData[`comment_line_${num}` as keyof CertificateFormData] as string}
                    onChange={(e) => handleTextInput(`comment_line_${num}`, e.target.value, 65)}
                    disabled={fieldsLocked || !isEditable}
                    readOnly={isAutoPopulated}
                    maxLength={65}
                    placeholder={`Comment line ${num}`}
                    className={isAutoPopulated ? 'auto-populated' : 'user-editable'}
                  />
                  <span className="char-count">
                    {(formData[`comment_line_${num}` as keyof CertificateFormData] as string).length}/65
                  </span>
                </div>
              );
            })}
            </div>
          </div>

          <div className="abbreviation-lines">
            <h4>Abbreviation Lines (31 characters max, ASCII only)</h4>
            <div className="abbreviation-lines-grid">
              {Array.from({ length: 13 }, (_, i) => i + 1).map(num => {
              const fieldKey = `abbreviation_line_${num}` as keyof typeof fieldStates;
              const fieldState = fieldStates[fieldKey];
              const isAutoPopulated = fieldState?.isAutoPopulated || false;
              const isEditable = fieldState?.isEditable !== false;
              
              return (
                <div key={num} className={`form-field ${isAutoPopulated ? 'has-auto-populated' : 'has-user-editable'}`}>
                  <label>Abbreviation Line {num}</label>
                  <input
                    type="text"
                    value={formData[`abbreviation_line_${num}` as keyof CertificateFormData] as string}
                    onChange={(e) => handleTextInput(`abbreviation_line_${num}`, e.target.value, 31)}
                    disabled={fieldsLocked || !isEditable}
                    readOnly={isAutoPopulated}
                    maxLength={31}
                    placeholder={`Abbreviation line ${num}`}
                    className={isAutoPopulated ? 'auto-populated' : 'user-editable'}
                  />
                  <span className="char-count">
                    {(formData[`abbreviation_line_${num}` as keyof CertificateFormData] as string).length}/31
                  </span>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        {/* Image Upload Section */}
        {showImageUpload && (
          <div className="form-section image-upload-section">
            <h3>Certificate Image Upload</h3>
            <p>Upload requirements: JPG or WEBP format, maximum 500KB</p>

            {/* Upload Method Toggle */}
            <div className="upload-method-toggle">
              <button 
                type="button"
                className={`method-btn ${uploadMethod === 'computer' ? 'active' : ''}`}
                onClick={() => setUploadMethod('computer')}
              >
                📤 Upload from Computer
              </button>
              <button 
                type="button"
                className={`method-btn ${uploadMethod === 'staging' ? 'active' : ''}`}
                onClick={() => setUploadMethod('staging')}
                disabled={imageUpload.uploaded}
                title={imageUpload.uploaded ? '⚠️ Cannot switch to staging after image is uploaded to S3' : 'Select image from staging folder'}
              >
                🗂️ Select from Staging
              </button>
            </div>

            {uploadMethod === 'computer' ? (
              <>
                {/* Hidden file input - always available for both initial selection and replace */}
                <input
                  id="imageInput"
                  type="file"
                  accept="image/jpeg,image/webp"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />

                {!imageUpload.selected ? (
              // No image selected - show file selection area
              <div className="upload-area" onClick={() => document.getElementById('imageInput')?.click()}>
                <div className="upload-prompt">
                  <p>Click or drag and drop to select image</p>
                  <p className="upload-rules">JPG/WEBP only, Max 500KB</p>
                </div>
              </div>
            ) : (
              // Image selected - show preview and actions
              <div className="image-selected">
                <div className="image-preview">
                  <img src={URL.createObjectURL(imageUpload.file!)} alt="Certificate Preview" />
                </div>
                <div className="image-info">
                  <p><strong>File:</strong> {imageUpload.file?.name}</p>
                  <p><strong>Size:</strong> {Math.round((imageUpload.file?.size || 0) / 1024)}KB</p>
                  {imageUpload.uploaded && (
                    <p><strong>System:</strong> {imageUpload.systemFilename}</p>
                  )}
                  <p><strong>Status:</strong> {
                    imageUpload.uploading ? 'Uploading...' :
                    imageUpload.uploaded ? '✅ Uploaded to server - Form locked' : 'Ready to upload'
                  }</p>
                </div>
                <div className="image-actions">
                  {!imageUpload.uploaded && !imageUpload.uploading && (
                    <button type="button" onClick={handleImageUpload} className="upload-image-btn">
                      Upload Image
                    </button>
                  )}
                  {!imageUpload.uploaded && (
                    <>
                      <button type="button" onClick={() => {
                        console.log('🔍 Replace button clicked')
                        const input = document.getElementById('imageInput') as HTMLInputElement
                        if (input) {
                          console.log('🔍 File input found, triggering click')
                          input.click()
                        } else {
                          console.error('🔍 File input not found!')
                        }
                      }} className="replace-image-btn">
                        Replace Image
                      </button>
                      <button type="button" onClick={handleRemoveImage} className="remove-image-btn">
                        Remove Image
                      </button>
                    </>
                  )}
                </div>
                {imageUpload.uploading && (
                  <div className="uploading">
                    <div className="spinner"></div>
                    <p>Uploading to server...</p>
                  </div>
                )}
              </div>
            )}
              </>
            ) : (
              // Staging image selector
              <StagingImageUpload
                location={formData.created_location || 'DUBAI'}
                onImageSelected={handleStagingImageSelected}
                onCancel={() => setUploadMethod('computer')}
                disableCancel={imageUpload.uploaded}
              />
            )}
          </div>
        )}

        {/* Validation Status - Show when image is uploaded but validations don't pass */}
        {imageUpload.uploaded && !certificateCreated && !checkAllValidationsPass() && (
          <div className="form-section">
            <div className="validation-message error">
              <strong>Complete all required fields before saving:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                {!checkMandatoryFieldsValid() && (
                  <li>Complete all mandatory fields (Certificate ID, Gemstone Types, Weights)</li>
                )}
                {formData.gemstone_selections.map((selection, index) => {
                  if (!selection.gemstone_type) return null
                  
                  let missingFields = []
                  
                  if (selection.gemstone_type.includes('PEARL')) {
                    if (!selection.measurement_range && !selection.measurement_value) {
                      missingFields.push('measurement (diameter range or single value)')
                    }
                  } else if (selection.gemstone_type.startsWith('DIAMOND_')) {
                    if (!selection.carat_weight && !selection.measurement_value) {
                      missingFields.push('carat weight')
                    }
                    if (!selection.growth_origin) {
                      missingFields.push('growth origin')
                    }
                    if (!selection.diamond_color_range && !selection.diamond_color_fixed && 
                        !selection.diamond_clarity_range && !selection.diamond_clarity_fixed) {
                      missingFields.push('color or clarity selection')
                    }
                  } else {
                    if (!selection.carat_weight && !selection.measurement_value) {
                      missingFields.push('carat weight')
                    }
                    if (!selection.growth_origin) {
                      missingFields.push('growth origin')
                    }
                  }
                  
                  if (missingFields.length > 0) {
                    return (
                      <li key={index}>
                        {selection.gemstone_type}: {missingFields.join(', ')}
                      </li>
                    )
                  }
                  return null
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Save Certificate Button */}
        {imageUpload.uploaded && !certificateCreated && checkAllValidationsPass() && (
          <div className="form-section">
            <button
              type="button"
              onClick={handleSaveCertificate}
              className="save-certificate-btn"
            >
              Save Certificate
            </button>
          </div>
        )}

        {/* Certificate Created */}
        {certificateCreated && (
          <div className="form-section certificate-created">
            <h3>Certificate Created Successfully!</h3>
            <p><strong>Certificate ID:</strong> {formData.cert_id}</p>
            <p><strong>Certificate UUID:</strong> {certificateUuid}</p>
            <div className="certificate-actions">
              <button
                type="button"
                onClick={openPublicCertificate}
                className="open-public-btn"
              >
                Open Public Certificate
              </button>
              <button
                type="button"
                onClick={handleCreateNewCertificate}
                className="create-new-btn"
              >
                Create New Certificate
              </button>
            </div>
          </div>
        )}

        {/* Verification Checkpoint */}
        {showVerificationCheckpoint && (
          <div className="form-section verification-checkpoint">
            <h3>Verification Checkpoint</h3>
            <p>Please review the certificate details before final submission.</p>

            <div className="verification-fields">
              <div className="form-field">
                <label>Certificate ID:</label>
                <input
                  type="text"
                  value={formData.cert_id}
                  onChange={(e) => handleVerificationFieldEdit('cert_id', e.target.value)}
                  onKeyDown={(e) => {
                    // Allow control keys (backspace, delete, arrow keys, etc.)
                    if (e.ctrlKey || e.metaKey || e.altKey || 
                        ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(e.key)) {
                      return
                    }
                    // Only allow A-Z and 0-9
                    if (!/^[A-Za-z0-9]$/.test(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  disabled={isSubmitting}
                  maxLength={15}
                  className={
                    validationState.cert_id?.isChecking ? 'checking' :
                    validationState.cert_id?.isValid === true ? 'valid' :
                    validationState.cert_id?.isValid === false ? 'invalid' : ''
                  }
                />
                {validationState.cert_id && (
                  <div className={`validation-message ${validationState.cert_id.isValid ? 'valid' : 'invalid'}`}>
                    {validationState.cert_id.isChecking ? 'Checking availability...' : validationState.cert_id.message}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label>Confirm Certificate ID:</label>
                <input
                  type="text"
                  value={formData.cert_id_confirm || ''}
                  onChange={(e) => handleVerificationFieldEdit('cert_id_confirm', e.target.value)}
                  onKeyDown={(e) => {
                    // Allow control keys (backspace, delete, arrow keys, etc.)
                    if (e.ctrlKey || e.metaKey || e.altKey || 
                        ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(e.key)) {
                      return
                    }
                    // Only allow A-Z and 0-9
                    if (!/^[A-Za-z0-9]$/.test(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  onPaste={(e) => {
                    // Prevent paste operations
                    e.preventDefault()
                    addStatusMessage('Please type the Certificate ID confirmation manually - copy/paste is not allowed', 'warning')
                  }}
                  onDrop={(e) => {
                    // Prevent drag and drop
                    e.preventDefault()
                    addStatusMessage('Please type the Certificate ID confirmation manually - drag/drop is not allowed', 'warning')
                  }}
                  disabled={isSubmitting}
                  maxLength={15}
                  className={
                    formData.cert_id_confirm && formData.cert_id && formData.cert_id_confirm === formData.cert_id && 
                    validationState.cert_id?.isValid === true ? 'valid' :
                    formData.cert_id_confirm && formData.cert_id && 
                    (formData.cert_id_confirm !== formData.cert_id || validationState.cert_id?.isValid === false) ? 'invalid' : ''
                  }
                />
                {formData.cert_id_confirm && formData.cert_id && (
                  <div className={`validation-message ${
                    formData.cert_id_confirm === formData.cert_id && 
                    validationState.cert_id?.isValid === true ? 'valid' : 'invalid'
                  }`}>
                    {formData.cert_id_confirm === formData.cert_id ? 
                      (validationState.cert_id?.isValid === true ? 
                        '✓ Certificate IDs match' : 
                        'Certificate ID matches but Certificate ID already exists. Please provide a unique Certificate ID') : 
                      '✗ Certificate IDs do not match'}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label>Metal Composition:</label>
                <div className="radio-group">
                  {referenceData.metal_compositions.map(metal => (
                    <label 
                      key={metal.metal_composition}
                      className={formData.metal_composition === metal.metal_composition ? 'checked' : ''}
                    >
                      <input
                        type="radio"
                        name="verification_metal_composition"
                        value={metal.metal_composition}
                        checked={formData.metal_composition === metal.metal_composition}
                        onChange={(e) => handleVerificationFieldEdit('metal_composition', e.target.value)}
                        disabled={fieldsLocked || isSubmitting}
                      />
                      <span>{metal.metal_composition} {metal.abbreviation && `(${metal.abbreviation})`}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-field">
                <label>Item Gross Weight (Grams):</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  pattern="[0-9]+(\.[0-9]+)?"
                  value={formData.item_gross_weight}
                  onChange={(e) => handleVerificationFieldEdit('item_gross_weight', e.target.value)}
                  onBlur={(e) => handleWeightBlur('item_gross_weight', e.target.value)}
                  disabled={isSubmitting}
                  className={
                    validationState.item_gross_weight?.isValid === true ? 'valid' :
                    validationState.item_gross_weight?.isValid === false ? 'invalid' : ''
                  }
                  placeholder="Weight"
                />
                {validationState.item_gross_weight && (
                  <div className={`validation-message ${validationState.item_gross_weight.isValid ? 'valid' : 'invalid'}`}>
                    {validationState.item_gross_weight.message}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label>Gemstone Growth Origin:</label>
                <div className="radio-group">
                  {referenceData.growth_origins.map(origin => (
                    <label 
                      key={origin.growth_origin}
                      className={formData.gemstone_growth_origin === origin.growth_origin ? 'checked' : ''}
                    >
                      <input
                        type="radio"
                        name="verification_growth_origin"
                        value={origin.growth_origin}
                        checked={formData.gemstone_growth_origin === origin.growth_origin}
                        onChange={(e) => handleVerificationFieldEdit('growth_origin', e.target.value)}
                        disabled={fieldsLocked || isSubmitting}
                      />
                      <span>{origin.growth_origin} {origin.abbreviation && `(${origin.abbreviation})`}</span>
                    </label>
                  ))}
                </div>
              </div>



              <div className="form-field">
                <label>Card Design Code:</label>
                <div className="single-select">
                  <label>
                    <input
                      type="radio"
                      name="verification_card_design"
                      value="EUROPEANGEMOLOGICALAB"
                      checked={formData.card_design_code === 'EUROPEANGEMOLOGICALAB'}
                      onChange={(e) => handleVerificationFieldEdit('card_design_code', e.target.value)}
                      disabled={fieldsLocked || isSubmitting}
                    />
                    <span>European Gemological Laboratory</span>
                  </label>
                </div>
              </div>

              <div className="comment-lines">
                <h4>Comment Lines (65 characters max, ASCII only)</h4>
                {[1, 2, 3].map(num => {
                  const fieldKey = `comment_line_${num}` as keyof typeof fieldStates;
                  const fieldState = fieldStates[fieldKey];
                  const isAutoPopulated = fieldState?.isAutoPopulated || false;
                  const isEditable = fieldState?.isEditable !== false;
                  
                  return (
                    <div key={num} className={`form-field ${isAutoPopulated ? 'has-auto-populated' : 'has-user-editable'}`}>
                      <label>Comment Line {num}:</label>
                      <input
                        type="text"
                        value={formData[`comment_line_${num}` as keyof CertificateFormData] as string}
                        onChange={(e) => handleVerificationFieldEdit(`comment_line_${num}`, e.target.value)}
                        disabled={isSubmitting || !isEditable}
                        readOnly={isAutoPopulated}
                        maxLength={65}
                        placeholder={`Comment line ${num}`}
                        className={isAutoPopulated ? 'auto-populated' : 'user-editable'}
                      />
                      <span className="char-count">
                        {(formData[`comment_line_${num}` as keyof CertificateFormData] as string).length}/65
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="abbreviation-lines">
                <h4>Abbreviation Lines (31 characters max, ASCII only)</h4>
                {Array.from({ length: 13 }, (_, i) => i + 1).map(num => {
                  const fieldKey = `abbreviation_line_${num}` as keyof typeof fieldStates;
                  const fieldState = fieldStates[fieldKey];
                  const isAutoPopulated = fieldState?.isAutoPopulated || false;
                  const isEditable = fieldState?.isEditable !== false;
                  
                  return (
                    <div key={num} className={`form-field ${isAutoPopulated ? 'has-auto-populated' : 'has-user-editable'}`}>
                      <label>Abbreviation Line {num}:</label>
                      <input
                        type="text"
                        value={formData[`abbreviation_line_${num}` as keyof CertificateFormData] as string}
                        onChange={(e) => handleVerificationFieldEdit(`abbreviation_line_${num}`, e.target.value)}
                        disabled={isSubmitting || !isEditable}
                        readOnly={isAutoPopulated}
                        maxLength={31}
                        placeholder={`Abbreviation line ${num}`}
                        className={isAutoPopulated ? 'auto-populated' : 'user-editable'}
                      />
                      <span className="char-count">
                        {(formData[`abbreviation_line_${num}` as keyof CertificateFormData] as string).length}/31
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="verification-actions">
              <button
                type="button"
                onClick={handleVerifiedSubmit}
                className="submit-verified-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Certificate'}
              </button>
              <button
                type="button"
                onClick={handleVerificationCancel}
                className="cancel-verification-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Bottom Status Messages - Duplicate for better UX */}
        {statusMessages.length > 0 && (
          <div className="status-messages bottom-status-messages">
            {statusMessages.map((msg, index) => (
              <div key={index} className={`status-message ${msg.type}`}>
                {msg.message}
              </div>
            ))}
          </div>
        )}
        </div>
        </div>

        {/* Divider */}
        <div className="split-screen-divider"></div>

        {/* Right Side - Live Preview */}
        <div className="live-preview-side">
          <CertificateLivePreview 
            formData={formData}
            imageUpload={imageUpload}
          />
        </div>
      </div>

      {/* Re-Authentication Modal */}
      <ReAuthModal
        isOpen={showReAuthModal}
        username={user?.username || ''}
        onSuccess={handleReAuthSuccess}
        onCancel={handleReAuthCancel}
        onRedirectToLogin={handleReAuthRedirect}
      />

      {/* Session Extension Modal */}
      <SessionExtensionModal
        isOpen={showSessionExtension}
        username={user?.username || ''}
        timeLeft={sessionTimeLeft}
        onExtend={handleSessionExtend}
        onCancel={handleSessionExtensionCancel}
        onLogout={handleSessionLogout}
      />

      {/* Image Crop Modal */}
      {showCropModal && selectedFileForCrop && (
        <ImageCropModal
          originalFile={selectedFileForCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  )
} 